/* audio-tag-writer.js — Timbrica Audio Metadata engine (pure-JS, lossless WRITE).
 *
 * Sibling of image-metadata-engine.js. Where that tool writes EXIF via piexif,
 * this one writes audio tags by SPLICING the tag region only — the encoded audio
 * frames are copied byte-for-byte, never decoded/re-encoded. Result: changing a
 * title or cover is instant, lossless, needs NO 30 MB FFmpeg WASM, and can't
 * fail with FFmpeg's "Exactly one MP3 audio stream is required" mux errors.
 *
 * WHY this exists (root cause, 2026-07-15): the old tool routed every save
 * through FFmpeg-WASM warmed on file-pick — on 4 GB mobile that memory spike let
 * the OS discard the tab while the file-picker was open, so the page reloaded and
 * "the site kept resetting" (bug-report + analytics: upload_no_pick_followup 665,
 * mobile pick->complete 76-86% vs 89-93% desktop, saves waiting 3-103 s).
 *
 * Formats written in pure-JS: MP3 (ID3v2.3 + APIC), FLAC (Vorbis comment +
 * PICTURE), WAV (LIST/INFO + an `id3 ` chunk carrying text AND APIC), M4A/MP4
 * (moov/udta/meta/ilst), OGG Vorbis/Opus (comment header +
 * METADATA_BLOCK_PICTURE). Everything else (WMA/AIFF + format conversion)
 * stays on the lazy FFmpeg fallback in the blade — WRITABLE below is the gate.
 *
 * Exposes window.AudioTag; also CommonJS-exported so the write core is
 * unit-tested in Node against real files (see tests/audio-tag-writer.test.mjs).
 */
(function (root, factory) {
    'use strict';
    var mod = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = mod;
    if (typeof window !== 'undefined') window.AudioTag = mod;
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    /* ════════════════ BINARY HELPERS ════════════════ */
    // Chunked to avoid "Maximum call stack" on String.fromCharCode.apply (big files).
    function ab2bstr(buf) {
        var u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
        var s = '', CH = 0x8000;
        for (var i = 0; i < u8.length; i += CH) {
            s += String.fromCharCode.apply(null, u8.subarray(i, Math.min(i + CH, u8.length)));
        }
        return s;
    }
    function bstr2u8(bstr) {
        var len = bstr.length, u8 = new Uint8Array(len);
        for (var i = 0; i < len; i++) u8[i] = bstr.charCodeAt(i) & 0xff;
        return u8;
    }
    function concat(parts) {
        var n = 0, i;
        for (i = 0; i < parts.length; i++) n += parts[i].length;
        var out = new Uint8Array(n), off = 0;
        for (i = 0; i < parts.length; i++) { out.set(parts[i], off); off += parts[i].length; }
        return out;
    }
    function u32be(n) { return Uint8Array.of((n >>> 24) & 0xff, (n >>> 16) & 0xff, (n >>> 8) & 0xff, n & 0xff); }
    function u24be(n) { return Uint8Array.of((n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff); }
    function u16be(n) { return Uint8Array.of((n >> 8) & 0xff, n & 0xff); }
    function u32le(n) { return Uint8Array.of(n & 0xff, (n >>> 8) & 0xff, (n >>> 16) & 0xff, (n >>> 24) & 0xff); }
    function rdU32be(u8, o) { return (u8[o] * 0x1000000) + (u8[o + 1] << 16) + (u8[o + 2] << 8) + u8[o + 3]; }
    function rdU32le(u8, o) { return (u8[o]) + (u8[o + 1] << 8) + (u8[o + 2] << 16) + (u8[o + 3] * 0x1000000); }
    function ascii(str) { var u = new Uint8Array(str.length); for (var i = 0; i < str.length; i++) u[i] = str.charCodeAt(i) & 0xff; return u; }
    function utf8(str) {
        if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(str);
        return bstr2u8(unescape(encodeURIComponent(str)));
    }
    /* Текст тега, записанный одним байтом на знак. Слот называется «ISO-8859-1», но
       писали его программы Windows, и их 0x80–0x9F — это «’», «–», «…» из
       Windows-1252, а не управляющие коды: так же читает эту метку любой браузер.
       Чтение обратимо (ни один байт не теряется), поэтому текст, набранный на самом
       деле в другой кодировке, можно прочитать заново — см. tag-text-encoding.js. */
    function cp1252decode(u8) {
        if (typeof TextDecoder !== 'undefined') { try { return new TextDecoder('windows-1252').decode(u8); } catch (_) { /* нет метки — ниже */ } }
        return ab2bstr(u8);
    }
    /* Текст, объявленный как UTF-8 (комментарии Vorbis, APE, кадр ID3v2.4). Если байты им
       не являются — старая программа положила туда системную кодировку, — они читаются
       как однобайтный слот: знаки замены (U+FFFD) вместо названия нельзя ни показать, ни починить. */
    function utf8decode(u8) {
        if (typeof TextDecoder !== 'undefined') {
            try { return new TextDecoder('utf-8', { fatal: true }).decode(u8); } catch (_) { return cp1252decode(u8); }
        }
        try { return decodeURIComponent(escape(ab2bstr(u8))); } catch (_) { return ab2bstr(u8); }
    }
    // synchsafe 28-bit (ID3v2 tag size)
    function synchsafe(n) { return Uint8Array.of((n >>> 21) & 0x7f, (n >>> 14) & 0x7f, (n >>> 7) & 0x7f, n & 0x7f); }
    function rdSynchsafe(u8, o) { return ((u8[o] & 0x7f) << 21) | ((u8[o + 1] & 0x7f) << 14) | ((u8[o + 2] & 0x7f) << 7) | (u8[o + 3] & 0x7f); }
    function isLatin1(str) { for (var i = 0; i < str.length; i++) if (str.charCodeAt(i) > 0xff) return false; return true; }
    /* Строго ASCII (≤0x7F). Для ID3 годится latin-1 целиком — там кодировка
       ОБЪЯВЛЕНА байтом. Для RIFF LIST/INFO — нет: диапазон 0x80–0xFF это
       ровно то, что системная ANSI-страница читает по-своему (é = 0xE9 в
       cp1252, но другая буква в cp1251), поэтому туда пускаем только ASCII. */
    function isLatin1Ascii(str) { for (var i = 0; i < str.length; i++) if (str.charCodeAt(i) > 0x7f) return false; return true; }
    function utf16leBom(str) {
        var out = new Uint8Array(2 + str.length * 2);
        out[0] = 0xff; out[1] = 0xfe;
        for (var i = 0; i < str.length; i++) { var c = str.charCodeAt(i); out[2 + i * 2] = c & 0xff; out[2 + i * 2 + 1] = (c >> 8) & 0xff; }
        return out;
    }

    /* ════════════════ FORMAT DETECTION (magic bytes) ════════════════ */
    function detectFormat(u8) {
        if (!u8 || u8.length < 12) return 'unknown';
        var b = u8;
        // Skip a leading ID3v2 tag to look at the real signature underneath (some
        // WAV/FLAC-in-the-wild carry an ID3 chunk; MP3 obviously does).
        if (b[0] === 0x49 && b[1] === 0x44 && b[2] === 0x33) return 'mp3'; // 'ID3'
        if (b[0] === 0xFF && (b[1] & 0xE0) === 0xE0) return 'mp3';         // MPEG frame sync
        if (b[0] === 0x66 && b[1] === 0x4C && b[2] === 0x61 && b[3] === 0x43) return 'flac'; // 'fLaC'
        if (b[0] === 0x4F && b[1] === 0x67 && b[2] === 0x67 && b[3] === 0x53) return 'ogg';  // 'OggS'
        if (b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46) {              // 'RIFF'
            if (b[8] === 0x57 && b[9] === 0x41 && b[10] === 0x56 && b[11] === 0x45) return 'wav'; // 'WAVE'
            return 'unknown';
        }
        if (b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70) { // 'ftyp' → ISO-BMFF
            var brand = String.fromCharCode(b[8], b[9], b[10], b[11]).toLowerCase();
            /* QuickTime .mov — named apart so the MIME is right, written by the
               same moov/udta/meta/ilst writer as MP4.
               ⚠️ Until 2026-09-25 this said «iTunes-style ilst is simply not read
               in MOV» (measured 17.09: a .mov came back with the title gone) and
               sent MOV to the FFmpeg lane. The measurement was true, the cause
               was not: that .mov had no iTunes meta of its own, so it got the
               size-less `hdlr` (defect 1 in writeM4a) — every MP4 of that shape
               lost its tags the same way. Written correctly, ffprobe AND Windows
               Explorer read MOV title and cover (Explorer shows the cover as the
               thumbnail), while the FFmpeg lane's mov muxer drops an attached
               picture without a word. */
            if (/^qt/.test(brand)) return 'mov';
            if (/m4a|m4b|m4p|mp4|isom|dash|3gp/.test(brand)) return 'm4a';
            return 'm4a';
        }
        if (b[0] === 0x46 && b[1] === 0x4F && b[2] === 0x52 && b[3] === 0x4D) return 'aiff'; // 'FORM'
        return 'unknown';
    }

    // Only these are written by the pure-JS core; the blade routes the rest to
    // its lazy FFmpeg fallback. m4a stays gated behind its own test verdict.
    var WRITABLE = { mp3: true, flac: true, wav: true, m4a: true, mov: true, ogg: true, aiff: false, unknown: false };
    // Which formats can carry embedded cover art in this engine. WAV carries it
    // as an APIC frame in the `id3 ` chunk — who shows it and who never will is
    // measured in writeWav. This table is the ONE answer the page asks.
    var COVER_CAPABLE = { mp3: true, flac: true, m4a: true, mov: true, wav: true, ogg: true };

    /* Canonical friendly-key set the whole tool speaks. Empty string / absent =
       "no such tag" (the caller merges original+edits and passes the full set;
       we write only the non-empty ones — same semantics as FFmpeg -map_metadata -1). */
    function cleanSet(set) {
        var out = {};
        set = set || {};
        for (var k in set) {
            if (!Object.prototype.hasOwnProperty.call(set, k)) continue;
            var v = set[k];
            if (v === undefined || v === null) continue;
            v = String(v).trim();
            if (v !== '') out[k] = v;
        }
        return out;
    }

    /* ════════════════════════════════════════════════════════════════════
       MP3 — ID3v2.3 (+ APIC); the frames the editor does not manage are kept
       (see «ONE WALK OF THE TAG»), and so is what stands after the audio
       (see «THE TAGS AFTER THE AUDIO»).
       ════════════════════════════════════════════════════════════════════ */
    var ID3_FRAMES = {
        title: 'TIT2', artist: 'TPE1', album: 'TALB', albumArtist: 'TPE2',
        year: 'TYER', genre: 'TCON', track: 'TRCK', disc: 'TPOS',
        composer: 'TCOM', copyright: 'TCOP', isrc: 'TSRC', encoder: 'TSSE'
    };

    /* ════════════════════════════════════════════════════════════════════
       MP3 — THE TAGS AFTER THE AUDIO: ID3v1, APEv2, Lyrics3

       An MP3 can say the same thing in three places at once. The rule of this
       engine, said for such a file: an edit leaves the editor's fields in ONE
       place — the ID3v2 tag in front — and keeps what the editor does not
       manage; a strip removes all of it.

         ID3v1    128 bytes: title, artist, album, year, comment, track,
                  genre — cut to 30 characters, in no declared encoding.
                  Every one is the editor's field: the page reads them where
                  ID3v2 has none, the save writes them into ID3v2, and the
                  copy goes. Left behind it would answer for a field the
                  person has just cleared: Windows shows the ID3v1 album and
                  year wherever ID3v2 has none (measured 2026-10-07).
         APEv2    key=value items. The editor's keys (Title, Artist, …) are
                  read the same way and dropped the same way. Every other item
                  — ReplayGain, MP3Gain's undo record, MusicBrainz ids — is
                  carried byte for byte. Until 2026-10-07 any save threw the
                  whole tag away, and nothing read it: an MP3 tagged only in
                  APE opened as untagged.
                  A picture stored as an item follows the picture operation:
                  untouched — kept; replaced or removed — gone.
         Lyrics3  is found only right before an ID3v1 tag, so where it is
                  present that pair is carried as it stands.

       The blocks keep the order they had in the file.
       ════════════════════════════════════════════════════════════════════ */
    var APE_MAP = {
        title: ['TITLE'], artist: ['ARTIST'], album: ['ALBUM'], albumArtist: ['ALBUM ARTIST', 'ALBUMARTIST'],
        year: ['YEAR'], genre: ['GENRE'], track: ['TRACK', 'TRACKNUMBER'], disc: ['DISC', 'DISCNUMBER'],
        composer: ['COMPOSER'], copyright: ['COPYRIGHT'], isrc: ['ISRC'], comment: ['COMMENT'],
        lyrics: ['LYRICS', 'UNSYNCEDLYRICS', 'UNSYNCED LYRICS']
    };
    var APE_OWNED = {};
    (function () { for (var f in APE_MAP) for (var i = 0; i < APE_MAP[f].length; i++) APE_OWNED[APE_MAP[f][i]] = f; })();
    function apeIsPicture(key) { return key.indexOf('COVER ART') === 0; }
    function bytesAt(u8, off, str) {
        if (off < 0 || off + str.length > u8.length) return false;
        for (var i = 0; i < str.length; i++) if (u8[off + i] !== str.charCodeAt(i)) return false;
        return true;
    }
    /** The tags after the audio, in file order: {audioEnd, blocks:[{kind:'v1'|'ape'|'lyrics3', start, end}]}. */
    function mp3Tail(u8, audioStart) {
        var end = u8.length, blocks = [], guard = 0;
        while (guard++ < 8) {
            if (end - 128 >= audioStart && bytesAt(u8, end - 128, 'TAG')) {
                blocks.unshift({ kind: 'v1', start: end - 128, end: end }); end -= 128; continue;
            }
            if (end - 32 >= audioStart && bytesAt(u8, end - 32, 'APETAGEX')) {
                var items = end - rdU32le(u8, end - 32 + 12);                 // the size counts the items and the footer
                var header = items - 32 >= audioStart && bytesAt(u8, items - 32, 'APETAGEX');
                var from = header ? items - 32 : items;
                if (items <= end - 32 && from >= audioStart) {
                    blocks.unshift({ kind: 'ape', start: from, end: end, items: items, header: header }); end = from; continue;
                }
            }
            if (end - 15 >= audioStart && bytesAt(u8, end - 9, 'LYRICS200')) {
                // v2: six digits before the marker — the size of everything from LYRICSBEGIN up to them
                var n = parseInt(ab2bstr(u8.subarray(end - 15, end - 9)), 10), ls = end - 15 - n;
                if (n > 0 && ls >= audioStart && bytesAt(u8, ls, 'LYRICSBEGIN')) {
                    blocks.unshift({ kind: 'lyrics3', start: ls, end: end }); end = ls; continue;
                }
            }
            if (end - 20 >= audioStart && bytesAt(u8, end - 9, 'LYRICSEND')) {
                // v1: no size — LYRICSBEGIN stands within the 5100 bytes before the marker
                var floor = Math.max(audioStart, end - 9 - 5100 - 11), found = -1;
                for (var p = end - 20; p >= floor; p--) if (u8[p] === 0x4c && bytesAt(u8, p, 'LYRICSBEGIN')) { found = p; break; }
                if (found >= 0) { blocks.unshift({ kind: 'lyrics3', start: found, end: end }); end = found; continue; }
            }
            break;
        }
        return { audioEnd: end, blocks: blocks };
    }
    /** Items of an APE block: {version, complete, items:[{key (upper case), name, flags, value, raw}]}. */
    function apeParse(u8, b) {
        var footer = b.end - 32, count = rdU32le(u8, footer + 16), pos = b.items, items = [];
        for (var i = 0; i < count && pos + 9 <= footer; i++) {
            var vlen = rdU32le(u8, pos), k = pos + 8;
            while (k < footer && u8[k] !== 0) k++;
            if (k >= footer || k === pos + 8 || k + 1 + vlen > footer) break;
            var name = ab2bstr(u8.subarray(pos + 8, k));
            items.push({ key: name.toUpperCase(), name: name, flags: rdU32le(u8, pos + 4), value: u8.subarray(k + 1, k + 1 + vlen), raw: u8.subarray(pos, k + 1 + vlen) });
            pos = k + 1 + vlen;
        }
        return { version: rdU32le(u8, footer + 8), complete: items.length === count && pos === footer, items: items };
    }
    function apeBuild(version, raws, withHeader) {
        var body = concat(raws);
        function block(isHeader) {
            var flags = version >= 2000 ? (((withHeader ? 0x80000000 : 0) + (isHeader ? 0x20000000 : 0)) >>> 0) : 0;
            return concat([ascii('APETAGEX'), u32le(version), u32le(body.length + 32), u32le(raws.length), u32le(flags), new Uint8Array(8)]);
        }
        return withHeader ? concat([block(true), body, block(false)]) : concat([body, block(false)]);
    }
    /** What an edit leaves of an APE block — the items the editor does not manage; null when nothing is left. */
    function apeCarry(u8, b, dropPictures) {
        var whole = u8.subarray(b.start, b.end), t = apeParse(u8, b);
        if (!t.complete) return whole;                                  // cannot be walked: carried as it stands
        var keep = [];
        for (var i = 0; i < t.items.length; i++) {
            var it = t.items[i];
            if (APE_OWNED[it.key] || (dropPictures && apeIsPicture(it.key))) continue;
            keep.push(it.raw);
        }
        if (keep.length === t.items.length) return whole;               // nothing of the editor's in it
        return keep.length ? apeBuild(t.version, keep, b.header) : null;
    }
    // An item's text: UTF-8, several values separated by a null; binary and locator items have none.
    function apeText(item) {
        if ((item.flags >> 1) & 3) return null;
        var parts = utf8decode(item.value).split('\0'), out = [];
        for (var i = 0; i < parts.length; i++) { var s = parts[i].trim(); if (s && out.indexOf(s) < 0) out.push(s); }
        return out.join('; ');
    }
    /** What stands after the audio of an MP3: {v1, lyrics3, ape: {tags, raw} | null}. `tags` — the editor's
        fields an APE tag carries (the page shows them where ID3v2 has none); `raw` — every text item. */
    function readMp3Tail(u8) {
        var out = { v1: false, lyrics3: false, ape: null };
        if (!u8 || u8.length < 32) return out;
        var blocks = mp3Bounds(u8).tail;
        for (var i = 0; i < blocks.length; i++) {
            var b = blocks[i];
            if (b.kind === 'v1') out.v1 = true;
            else if (b.kind === 'lyrics3') out.lyrics3 = true;
            else if (!out.ape) {
                out.ape = { tags: {}, raw: {} };
                var t = apeParse(u8, b);
                if (!t.complete) continue;
                for (var k = 0; k < t.items.length; k++) {
                    if (apeIsPicture(t.items[k].key)) continue;         // a picture, whatever its flags say
                    var text = apeText(t.items[k]), f = APE_OWNED[t.items[k].key];
                    if (text === null) continue;
                    out.ape.raw[t.items[k].name] = text;
                    if (f && text && out.ape.tags[f] === undefined) out.ape.tags[f] = text;
                }
            }
        }
        return out;
    }

    // Bounds of the audio (MPEG) frames: after a front ID3v2, before the tags
    // that follow the audio. Also returns the front tag range so we can lift its APIC.
    function mp3Bounds(u8) {
        var start = 0, id3v2End = 0;
        if (u8[0] === 0x49 && u8[1] === 0x44 && u8[2] === 0x33) { // 'ID3'
            var size = rdSynchsafe(u8, 6);
            id3v2End = 10 + size;
            if (u8[5] & 0x10) id3v2End += 10; // footer
            start = Math.min(id3v2End, u8.length);
        }
        var tail = mp3Tail(u8, start);
        return { audioStart: start, audioEnd: tail.audioEnd, id3v2Start: 0, id3v2End: id3v2End, tail: tail.blocks };
    }

    // Extract the existing front-cover APIC (raw picture bytes + mime) for preserve.
    function mp3ExtractApic(u8, tagEnd) {
        if (!(u8[0] === 0x49 && u8[1] === 0x44 && u8[2] === 0x33)) return null;
        var ver = u8[3];
        var pos = 10;
        if (u8[5] & 0x40) { pos += (ver === 4 ? rdSynchsafe(u8, 10) : rdU32be(u8, 10)); } // ext header
        var end = tagEnd || (10 + rdSynchsafe(u8, 6));
        while (pos < end - 10) {
            var fid = String.fromCharCode(u8[pos], u8[pos + 1], u8[pos + 2], u8[pos + 3]);
            if (fid === '\0\0\0\0') break;
            var fsize = ver === 4 ? rdSynchsafe(u8, pos + 4) : rdU32be(u8, pos + 4);
            if (fsize <= 0 || pos + 10 + fsize > end) break;
            if (fid === 'APIC') {
                try {
                    var d = u8.subarray(pos + 10, pos + 10 + fsize);
                    var i = 1; // skip text-encoding byte
                    while (i < d.length && d[i] !== 0) i++;
                    var mime = ab2bstr(d.subarray(1, i)); i++;
                    i++; // picture type
                    // description (encoding-dependent terminator)
                    var enc = d[0];
                    if (enc === 1 || enc === 2) { while (i < d.length - 1 && !(d[i] === 0 && d[i + 1] === 0)) i++; i += 2; }
                    else { while (i < d.length && d[i] !== 0) i++; i++; }
                    return { mime: mime || 'image/jpeg', data: d.subarray(i).slice() };
                } catch (e) { return null; }
            }
            pos += 10 + fsize;
        }
        return null;
    }

    function id3TextFrame(id, text) {
        var body;
        if (isLatin1(text)) body = concat([Uint8Array.of(0x00), ascii(text)]);           // ISO-8859-1
        else body = concat([Uint8Array.of(0x01), utf16leBom(text)]);                      // UTF-16 + BOM
        return concat([ascii(id), u32be(body.length), Uint8Array.of(0, 0), body]);
    }
    /* THE LANGUAGE OF A COMMENT IS NOT COSMETIC. COMM and USLT carry a
       three-letter language code, and Windows shows a comment only when that
       code is the person's own language or «XXX» — «language unknown» in the
       spec. Measured 2026-10-07 through the Windows 11 property system
       (Explorer → Properties → Details) on a Russian system: «rus» and «XXX»
       are shown; «eng», «deu», «fra», «und» and three zero bytes are not;
       FFmpeg's own comment (a TXXX frame) is not either. This engine wrote
       «eng» for every comment, in any language, so on any non-English Windows
       a comment typed here never appeared — and a comment Explorer itself had
       written («rus») vanished from it after any save.
       So: a frame that is rewritten keeps the language it had (`lang`), and a
       new comment is «XXX» — which is also simply true, we do not know the
       language. New lyrics stay «eng»: nothing measured says otherwise. */
    var ID3_NEW_LANG = { COMM: 'XXX', USLT: 'eng' };
    function id3CommLikeFrame(id, text, langCode) {
        // COMM / USLT: <enc><lang(3)><desc + term><text>
        var lang = ascii(/^[A-Za-z]{3}$/.test(langCode || '') ? langCode : ID3_NEW_LANG[id]);
        if (isLatin1(text)) {
            var body = concat([Uint8Array.of(0x00), lang, Uint8Array.of(0x00), ascii(text)]);
            return concat([ascii(id), u32be(body.length), Uint8Array.of(0, 0), body]);
        }
        var descTerm = Uint8Array.of(0xff, 0xfe, 0x00, 0x00); // empty UTF-16 desc + 0x0000
        var body2 = concat([Uint8Array.of(0x01), lang, descTerm, utf16leBom(text)]);
        return concat([ascii(id), u32be(body2.length), Uint8Array.of(0, 0), body2]);
    }
    function id3ApicFrame(mime, data) {
        var head = concat([Uint8Array.of(0x00), ascii(mime || 'image/jpeg'), Uint8Array.of(0x00, 0x03, 0x00)]);
        var body = concat([head, data]);
        return concat([ascii('APIC'), u32be(body.length), Uint8Array.of(0, 0), body]);
    }

    /* Готовый тег ID3v2.3 из уже собранных фреймов. Общий для MP3 (тег в
       голове файла) и для WAV (тот же тег ЦЕЛИКОМ внутри чанка `id3 `). */
    function id3v23Tag(frames) {
        var body = concat(frames);
        return concat([ascii('ID3'), Uint8Array.of(0x03, 0x00, 0x00), synchsafe(body.length), body]);
    }

    /* Текстовые фреймы по набору тегов — ровно те же, что пишет writeMp3.
       `langs` — язык, в котором комментарий и текст песни стояли в файле
       (id3Langs); без него — язык нового кадра. */
    function id3TextFrames(set, langs) {
        var frames = [];
        for (var key in ID3_FRAMES) if (set[key]) frames.push(id3TextFrame(ID3_FRAMES[key], set[key]));
        if (set.comment) frames.push(id3CommLikeFrame('COMM', set.comment, langs && langs.COMM));
        if (set.lyrics) frames.push(id3CommLikeFrame('USLT', set.lyrics, langs && langs.USLT));
        return frames;
    }

    /* ════════════════════════════════════════════════════════════════════
       ID3v2 — ONE WALK OF THE TAG, FOR THE READER AND FOR THE WRITER

       The rule is the one the Vorbis writers follow (see «ONE RULE» below): an
       edit rewrites the frames the editor shows and KEEPS every other frame;
       only a strip (ops.all) removes them. Until 2026-10-06 writeMp3 built the
       tag from the editor's fourteen fields and dropped the rest. Measured on
       prod, 16.09–06.10.2026: 5902 of 20 372 uploaded MP3s (29 %) carried
       frames the editor does not show — TXXX, GEOB (DJ cue points), TBPM,
       TKEY, POPM (ratings), CHAP/CTOC (audiobook and podcast chapters), SYLT —
       and every save, of anything, threw them away.

       The reader lives here too, because «which frame does the editor show»
       has to be answered once. The page used to read COMM/USLT as «encoding
       byte + text» and decoded the 3-byte language code as UTF-16: a Cyrillic
       comment or lyrics — written by this very engine, or by any tagger —
       came back as mojibake, and the next save wrote that mojibake into the
       file (reproduced 2026-10-06: «Комментарий кириллицей» → «︚омментарий…»).

       What is «the editor's»:
         text      the frames of ID3_FRAMES, plus TDRC (the v2.4 year);
         encoder   TSSE; TENC stands in for it only where TSSE is absent;
         comment   COMM with an EMPTY description (a described COMM —
                   iTunNORM, iTunSMPB — is another program's data);
         lyrics    USLT with an empty description, else the first USLT;
         picture   the first APIC. Left untouched it is rewritten as «Cover
                   (front)» — players ignore type «Other» — and the other
                   pictures stay; replaced or removed, all pictures go.

       v2.4 in, v2.3 out (v2.3 is what Windows and car stereos read): frame
       bodies are copied, and text in the two encodings v2.3 does not have
       (UTF-16BE without BOM, UTF-8) is re-encoded as UTF-16 with a BOM.
       A frame that is compressed, encrypted or grouped cannot be carried
       safely and is left out. v2.2 and a tag that cannot be walked are
       rebuilt as before.
       ════════════════════════════════════════════════════════════════════ */
    function id3Deunsync(u8) {
        var out = new Uint8Array(u8.length), o = 0;
        for (var i = 0; i < u8.length; i++) { out[o++] = u8[i]; if (u8[i] === 0xff && u8[i + 1] === 0x00) i++; }
        return out.subarray(0, o);
    }
    function id3IdAt(u8, p) {
        if (p + 4 > u8.length) return '';
        var s = '';
        for (var i = 0; i < 4; i++) { var c = u8[p + i]; if (!((c >= 0x41 && c <= 0x5a) || (c >= 0x30 && c <= 0x39))) return ''; s += String.fromCharCode(c); }
        return s;
    }
    // A frame may start at p, or the frames are over there (padding, end of tag).
    function id3Plausible(u8, p) { return p >= u8.length || u8[p] === 0 || (p + 10 <= u8.length && id3IdAt(u8, p) !== ''); }
    /** {ver, frames:[{id, body, carry}]} of the ID3v2.3 / v2.4 tag at the start of u8; null if none or not walkable. */
    function id3Walk(u8) {
        if (!u8 || u8.length < 10 || !(u8[0] === 0x49 && u8[1] === 0x44 && u8[2] === 0x33)) return null;
        var ver = u8[3], flags = u8[5];
        if (ver !== 3 && ver !== 4) return null;
        var body = u8.subarray(10, Math.min(10 + rdSynchsafe(u8, 6), u8.length));
        if ((flags & 0x80) && ver === 3) body = id3Deunsync(body);
        var pos = 0;
        if ((flags & 0x40) && body.length >= 4) pos = ver === 3 ? 4 + rdU32be(body, 0) : rdSynchsafe(body, 0);
        var frames = [];
        while (pos + 10 <= body.length) {
            var id = id3IdAt(body, pos);
            if (!id) break;
            var len = ver === 4 ? rdSynchsafe(body, pos + 4) : rdU32be(body, pos + 4);
            if (ver === 4) {
                // Old iTunes wrote plain sizes into v2.4 tags: take the reading that lands on a frame.
                var plain = rdU32be(body, pos + 4);
                if (plain !== len && !id3Plausible(body, pos + 10 + len) && pos + 10 + plain <= body.length && id3Plausible(body, pos + 10 + plain)) len = plain;
            }
            if (len <= 0 || pos + 10 + len > body.length) break;
            var f2 = body[pos + 9], data = body.subarray(pos + 10, pos + 10 + len), carry = true;
            if (ver === 3) { if (f2 & 0xe0) carry = false; }             // compression / encryption / grouping
            else if (f2 & 0x4c) carry = false;                           // grouping / compression / encryption
            else {
                if (f2 & 0x01) data = data.subarray(Math.min(4, data.length));   // data length indicator
                if ((f2 & 0x02) || (flags & 0x80)) data = id3Deunsync(data);
            }
            frames.push({ id: id, body: data, carry: carry });
            pos += 10 + len;
        }
        return { ver: ver, frames: frames };
    }
    function id3Decode(u8, enc) {
        if (!u8.length) return '';
        if (enc === 0) return cp1252decode(u8);
        if (enc === 3) return utf8decode(u8);
        var be = enc === 2, p = 0, s = '';
        if (enc === 1 && u8.length >= 2) {
            if (u8[0] === 0xfe && u8[1] === 0xff) { be = true; p = 2; }
            else if (u8[0] === 0xff && u8[1] === 0xfe) p = 2;
        }
        for (; p + 1 < u8.length; p += 2) s += String.fromCharCode(be ? (u8[p] << 8) | u8[p + 1] : u8[p] | (u8[p + 1] << 8));
        return s;
    }
    // Where the string that starts at «from» ends, and how long its terminator is.
    function id3Term(u8, enc, from) {
        var i;
        if (enc === 1 || enc === 2) { for (i = from; i + 1 < u8.length; i += 2) if (u8[i] === 0 && u8[i + 1] === 0) return { at: i, len: 2 }; return { at: u8.length, len: 0 }; }
        for (i = from; i < u8.length; i++) if (u8[i] === 0) return { at: i, len: 1 };
        return { at: u8.length, len: 0 };
    }
    function id3Trim(str) { return str.replace(/\0+$/, ''); }
    // T*** → text; several values (v2.4 separates them with a null) read « / »-separated.
    function id3TextOf(body) { return body.length < 1 ? '' : id3Trim(id3Decode(body.subarray(1), body[0])).replace(/\0/g, ' / '); }
    // COMM / USLT → {desc, text}
    function id3CommOf(body) {
        if (body.length < 4) return null;
        var enc = body[0], t = id3Term(body, enc, 4);
        return { desc: id3Decode(body.subarray(4, t.at), enc), text: id3Trim(id3Decode(body.subarray(Math.min(body.length, t.at + t.len)), enc)) };
    }
    // APIC → {mime, type, data}
    function id3ApicOf(body) {
        if (body.length < 4) return null;
        var m = id3Term(body, 0, 1);
        if (m.at + 2 > body.length) return null;
        var d = id3Term(body, body[0], m.at + 2);
        var data = body.subarray(Math.min(body.length, d.at + d.len));
        return data.length ? { mime: ab2bstr(body.subarray(1, m.at)) || 'image/jpeg', type: body[m.at + 1], data: data } : null;
    }
    // Index of the COMM / USLT frame the editor shows, or -1.
    function id3Pick(frames, id, anyDescription) {
        var first = -1;
        for (var i = 0; i < frames.length; i++) {
            if (frames[i].id !== id) continue;
            if (first < 0) first = i;
            var c = id3CommOf(frames[i].body);
            if (c && c.desc === '') return i;
        }
        return anyDescription ? first : -1;
    }
    /** What the editor shows of an ID3v2 tag: {version, tags, picture:{mime,type,data}|null, ids}. */
    function readId3(u8) {
        var walk = id3Walk(u8), out = { version: walk ? walk.ver : 0, tags: {}, picture: null, ids: [] };
        if (!walk) return out;
        var fr = walk.frames, t = out.tags, first = {}, i, v;
        for (i = 0; i < fr.length; i++) { out.ids.push(fr[i].id); if (!first[fr[i].id]) first[fr[i].id] = fr[i]; }
        for (var f in ID3_FRAMES) { if (first[ID3_FRAMES[f]]) { v = id3TextOf(first[ID3_FRAMES[f]].body); if (v) t[f] = v; } }
        var year = walk.ver === 4 ? (first.TDRC || first.TYER) : (first.TYER || first.TDRC);
        if (year) { v = id3TextOf(year.body); if (v) t.year = v; }
        if (!first.TSSE && first.TENC) { v = id3TextOf(first.TENC.body); if (v) t.encoder = v; }
        var ci = id3Pick(fr, 'COMM', false), li = id3Pick(fr, 'USLT', true), c;
        if (ci >= 0) { c = id3CommOf(fr[ci].body); if (c && c.text) t.comment = c.text; }
        if (li >= 0) { c = id3CommOf(fr[li].body); if (c && c.text) t.lyrics = c.text; }
        if (first.APIC) { var pic = id3ApicOf(first.APIC.body); if (pic) out.picture = { mime: pic.mime, type: pic.type, data: pic.data.slice() }; }
        return out;
    }
    var ID3_OWNED = { TDRC: 1 };
    (function () { for (var k in ID3_FRAMES) ID3_OWNED[ID3_FRAMES[k]] = 1; })();
    function id3Frame23(id, body) { return concat([ascii(id), u32be(body.length), Uint8Array.of(0, 0), body]); }
    // A v2.4 frame body as v2.3 can hold it: text in UTF-16BE-without-BOM or UTF-8 becomes UTF-16 with a BOM.
    function id3To23(id, body) {
        var enc = body[0];
        if (enc !== 2 && enc !== 3) return body;
        var Z = Uint8Array.of(0, 0), t, rest;
        if (id.charAt(0) === 'T' && id !== 'TXXX') return concat([Uint8Array.of(1), utf16leBom(id3Trim(id3Decode(body.subarray(1), enc)).replace(/\0/g, ' / '))]);
        if (id === 'TXXX' || id === 'WXXX') {
            t = id3Term(body, enc, 1); rest = body.subarray(Math.min(body.length, t.at + t.len));
            return concat([Uint8Array.of(1), utf16leBom(id3Decode(body.subarray(1, t.at), enc)), Z, id === 'WXXX' ? rest : utf16leBom(id3Trim(id3Decode(rest, enc)))]);
        }
        if ((id === 'COMM' || id === 'USLT') && body.length >= 4) {
            t = id3Term(body, enc, 4); rest = body.subarray(Math.min(body.length, t.at + t.len));
            return concat([Uint8Array.of(1), body.subarray(1, 4), utf16leBom(id3Decode(body.subarray(4, t.at), enc)), Z, utf16leBom(id3Trim(id3Decode(rest, enc)))]);
        }
        return body;                                                     // binary frames are carried as they are
    }
    /** The frames an edit keeps, as ID3v2.3 frames. coverOp is ops.cover: undefined = the shown picture is rewritten by the caller. */
    function id3Carry(walk, coverOp) {
        var fr = walk.frames, out = [], lyrics = id3Pick(fr, 'USLT', true), hasTsse = false, seenApic = false, i, c;
        for (i = 0; i < fr.length; i++) if (fr[i].id === 'TSSE') hasTsse = true;
        for (i = 0; i < fr.length; i++) {
            var f = fr[i];
            if (!f.carry || ID3_OWNED[f.id]) continue;
            if (f.id === 'TENC' && !hasTsse) continue;                   // it stood in for the encoder
            if (f.id === 'COMM' || f.id === 'USLT') {
                if (i === lyrics) continue;
                c = id3CommOf(f.body);
                if (!c || c.desc === '') continue;
            }
            if (f.id === 'APIC') {
                if (coverOp !== undefined) continue;
                if (!seenApic) { seenApic = true; continue; }
            }
            out.push(id3Frame23(f.id, walk.ver === 4 ? id3To23(f.id, f.body) : f.body));
        }
        return out;
    }
    // The language codes of the comment and the lyrics the editor shows — kept when those frames are rewritten.
    function id3Langs(walk) {
        var out = {};
        if (!walk) return out;
        var fr = walk.frames, at = { COMM: id3Pick(fr, 'COMM', false), USLT: id3Pick(fr, 'USLT', true) };
        for (var id in at) if (at[id] >= 0 && fr[at[id]].body.length >= 4) out[id] = ab2bstr(fr[at[id]].body.subarray(1, 4));
        return out;
    }
    // The picture the editor shows, for «cover untouched».
    function id3ShownPicture(walk) {
        if (!walk) return null;
        for (var i = 0; i < walk.frames.length; i++) if (walk.frames[i].id === 'APIC') { var p = id3ApicOf(walk.frames[i].body); return p ? { mime: p.mime, data: p.data.slice() } : null; }
        return null;
    }

    function writeMp3(u8, ops) {
        var set = cleanSet(ops.set);
        var b = mp3Bounds(u8);
        var audio = u8.subarray(b.audioStart, b.audioEnd);
        var walk = b.id3v2End ? id3Walk(u8.subarray(0, b.id3v2End)) : null;

        var frames = id3TextFrames(set, id3Langs(walk));
        if (walk && !ops.all) frames = frames.concat(id3Carry(walk, ops.cover));

        // cover: undefined = preserve existing, null = none, {data,mime} = replace
        var cover = ops.cover;
        if (cover === undefined) cover = id3ShownPicture(walk) || mp3ExtractApic(u8, b.id3v2End);
        if (cover && cover.data && cover.data.length) frames.push(id3ApicFrame(cover.mime, cover.data));

        // No frame left — no tag: a stripped MP3 is its audio frames and nothing else.
        var tag = frames.length ? id3v23Tag(frames) : new Uint8Array(0);

        // After the audio (see «THE TAGS AFTER THE AUDIO»): a strip leaves nothing there.
        var after = [], i;
        if (!ops.all) {
            var lyrics3 = false;
            for (i = 0; i < b.tail.length; i++) if (b.tail[i].kind === 'lyrics3') lyrics3 = true;
            for (i = 0; i < b.tail.length; i++) {
                var blk = b.tail[i];
                if (blk.kind === 'ape') { var ape = apeCarry(u8, blk, ops.cover !== undefined); if (ape) after.push(ape); }
                else if (lyrics3) after.push(u8.subarray(blk.start, blk.end));
            }
        }
        var out = concat([tag, audio].concat(after));
        if (ops.stamp) ops.stamped = stampMp3(out, tag.length, ops.stampSeed);
        return out;
    }

    /* ════════════════════════════════════════════════════════════════════
       A FRESH AUDIO FINGERPRINT FOR A NEW COVER (MP3)

       Yandex Music recognises an uploaded track by the bytes of its AUDIO
       FRAMES and shows the cover it stored the FIRST time those frames were
       uploaded — by anyone. Tags, the file name, the Info/Xing frame and what
       the sound decodes to are not part of it. Measured 2026-09-25 with real
       uploads to a private playlist:
         same frames, new tags + new cover (this tool's lossless edit)  → first cover
         same frames, new cover, new file name                          → first cover
         one byte changed in the Info frame only                        → first cover
         private bit set in ONE audio frame header (the first, or the last) → new cover
         M4A, FLAC: same audio, new cover                               → new cover (not affected)
       So a lossless tag edit — exactly what this engine is for — can never
       change the cover a person sees there («сто способов очистить кэш» did
       nothing: it is not a cache on their side). Songs from download sites
       were uploaded long before, so the cover shown is often a stranger's.

       The private bit is «for private use» and ignored by decoders (ISO
       11172-3 §2.4.2.3): the decoded sound stays bit-identical (checked by
       PCM md5 in the tests). A cover change writes a fresh random pattern into
       the private bits of the first STAMP_FRAMES audio frames, different from
       the pattern the input carried — so the SECOND cover change is a new
       upload too, not a flip back to the first file's bytes. Frames with a
       CRC are left alone (the bit is inside what the CRC covers), and so is
       the Info frame (not part of the fingerprint, and its LAME CRC covers
       its header). Returns how many frames were stamped (0 = could not).
       ════════════════════════════════════════════════════════════════════ */
    var STAMP_FRAMES = 32;
    var MPEG_KBPS = {
        v1l1: [0, 32, 64, 96, 128, 160, 192, 224, 256, 288, 320, 352, 384, 416, 448],
        v1l2: [0, 32, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320, 384],
        v1l3: [0, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320],
        v2l1: [0, 32, 48, 56, 64, 80, 96, 112, 128, 144, 160, 176, 192, 224, 256],
        v2l23: [0, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160]
    };
    var MPEG_HZ = { 3: [44100, 48000, 32000], 2: [22050, 24000, 16000], 0: [11025, 12000, 8000] };
    // {len, crc, ver, layer, mono} for a valid MPEG audio header at o, else null.
    // ver: 3 = MPEG-1, 2 = MPEG-2, 0 = MPEG-2.5; layer (raw bits): 1 = III, 2 = II, 3 = I.
    function mpegFrameAt(u8, o) {
        if (o + 4 > u8.length || u8[o] !== 0xFF || (u8[o + 1] & 0xE0) !== 0xE0) return null;
        var ver = (u8[o + 1] >> 3) & 3, layer = (u8[o + 1] >> 1) & 3;
        var bri = u8[o + 2] >> 4, sri = (u8[o + 2] >> 2) & 3, pad = (u8[o + 2] >> 1) & 1;
        if (ver === 1 || layer === 0 || bri === 0 || bri === 15 || sri === 3) return null; // reserved / free format
        var kbps = ver === 3 ? (layer === 3 ? MPEG_KBPS.v1l1 : layer === 2 ? MPEG_KBPS.v1l2 : MPEG_KBPS.v1l3)[bri]
                             : (layer === 3 ? MPEG_KBPS.v2l1 : MPEG_KBPS.v2l23)[bri];
        var hz = MPEG_HZ[ver][sri], bps = kbps * 1000;
        var len = layer === 3 ? (Math.floor(12 * bps / hz) + pad) * 4
            : (layer === 1 && ver !== 3) ? Math.floor(72 * bps / hz) + pad
            : Math.floor(144 * bps / hz) + pad;
        return { len: len, crc: !(u8[o + 1] & 1), ver: ver, layer: layer, mono: (u8[o + 3] >> 6) === 3 };
    }
    function isInfoFrame(u8, o, f) {
        if (f.layer !== 1) return false;
        var side = f.ver === 3 ? (f.mono ? 17 : 32) : (f.mono ? 9 : 17);
        var x = o + 4 + side, v = o + 36;
        var at = function (p, s) { return u8[p] === s.charCodeAt(0) && u8[p + 1] === s.charCodeAt(1) && u8[p + 2] === s.charCodeAt(2) && u8[p + 3] === s.charCodeAt(3); };
        return at(x, 'Xing') || at(x, 'Info') || at(v, 'VBRI');
    }
    function stampRandom(n, seed) {
        var bits = new Uint8Array(n);
        if (seed && seed.length) { for (var i = 0; i < n; i++) bits[i] = (seed[i >> 3] >> (i & 7)) & 1; return bits; }
        var c = (typeof crypto !== 'undefined' && crypto.getRandomValues) ? crypto : null;
        var r = new Uint8Array(Math.ceil(n / 8));
        if (c) c.getRandomValues(r); else for (var j = 0; j < r.length; j++) r[j] = Math.floor(Math.random() * 256);
        for (var k = 0; k < n; k++) bits[k] = (r[k >> 3] >> (k & 7)) & 1;
        return bits;
    }
    function stampMp3(out, audioStart, seed) {
        // walk frames that chain header-to-header (a real stream, not a stray 0xFFE)
        var picks = [], o = audioStart, first = null, guard = 0;
        while (picks.length < STAMP_FRAMES && guard++ < STAMP_FRAMES * 8) {
            var f = mpegFrameAt(out, o);
            if (!f || o + f.len > out.length) break;
            var next = mpegFrameAt(out, o + f.len);
            if (o + f.len < out.length && !next) break;
            if (first === null) first = f;
            if (f.ver !== first.ver || f.layer !== first.layer) break;
            if (!isInfoFrame(out, o, f) && !f.crc) picks.push(o);
            o += f.len;
        }
        if (!picks.length) return 0;
        var bits = stampRandom(picks.length, seed), same = true;
        for (var i = 0; i < picks.length; i++) if (((out[picks[i] + 2] & 1) !== bits[i])) { same = false; break; }
        if (same) bits[0] ^= 1; // never hand back the pattern the input already had
        for (var k = 0; k < picks.length; k++) out[picks[k] + 2] = (out[picks[k] + 2] & 0xFE) | bits[k];
        return picks.length;
    }

    /* ════════════════════════════════════════════════════════════════════
       FLAC — rewrite the editor's fields in VORBIS_COMMENT(4) and the PICTURE(6)
       blocks; STREAMINFO, the audio and every other block stay as they were.
       ════════════════════════════════════════════════════════════════════ */
    var VORBIS_MAP = {
        title: 'TITLE', artist: 'ARTIST', album: 'ALBUM', albumArtist: 'ALBUMARTIST',
        year: 'DATE', genre: 'GENRE', track: 'TRACKNUMBER', disc: 'DISCNUMBER',
        composer: 'COMPOSER', copyright: 'COPYRIGHT', isrc: 'ISRC', comment: 'COMMENT',
        lyrics: 'LYRICS', encoder: 'ENCODER'
    };
    /* ════════════════════════════════════════════════════════════════════
       VORBIS COMMENTS — ONE RULE FOR FLAC AND OGG

       An edit rewrites the fields the editor shows and KEEPS every other
       comment exactly as it stood; only a strip (`ops.all`) removes them.

       Until 2026-10-06 the two writers of this same format disagreed, each in
       its own harmful direction (measured on real files that day):
         · FLAC rebuilt the block from the editor's fourteen fields, so an
           edit of the title alone threw away everything else — ReplayGain,
           MusicBrainz ids, custom fields, and WAVEFORMATEXTENSIBLE_CHANNEL_MASK,
           the comment that says which speaker each channel belongs to. A 3.1
           track (FL FR FC LFE) saved through this editor came back declared
           as quad (FL FR BL BR): voice and bass sent to the rear speakers,
           with no error anywhere. Support chat 574 was on its way to exactly
           that file.
         · OGG kept foreign comments — and the page's «Очистить метаданные»
           called the same code without `all`, so a strip left CONTACT,
           LOCATION and any custom field in the file it handed back as clean.

       ALIASES. Taggers disagree on a few key names; FFmpeg itself writes a
       comment as DESCRIPTION. A field is read from its canonical key, else
       from an alias — and written back under THE KEY IT WAS READ FROM, so an
       edited value never sits next to its own stale copy, and every program
       that saw the field before the save still sees it after. Where the
       canonical key is present the alias is somebody's separate field and is
       left alone. A field the file did not have gets the canonical key.
       (For one day, 06–07.10.2026, the writer moved an alias to the canonical
       key. Measured 07.10 through the Windows 11 property system: in a FLAC,
       Explorer shows the comment from DESCRIPTION and from nothing else — not
       from COMMENT. Moving FFmpeg's DESCRIPTION to COMMENT took the comment
       out of the file's properties. YEAR and «ALBUM ARTIST» are read there
       as well as DATE and ALBUMARTIST, so keeping them costs nothing.)
       ════════════════════════════════════════════════════════════════════ */
    var VORBIS_ALIASES = {
        comment: ['DESCRIPTION'], year: ['YEAR'], albumArtist: ['ALBUM ARTIST', 'ALBUM_ARTIST'],
        lyrics: ['UNSYNCEDLYRICS', 'UNSYNCED LYRICS']
    };
    var VORBIS_PICTURE_KEYS = { METADATA_BLOCK_PICTURE: 1, COVERART: 1, COVERARTMIME: 1 };
    /* SEVERAL VALUES. A key may repeat — two ARTIST comments are two artists
       (Picard and foobar2000 write them so). Until 2026-10-07 the reader took
       the first and the writer put back one: an edit of the title removed the
       second artist. A repeated field reads as «a; b»; saved untouched it goes
       back as the comments it was, and edited — as what stands between the
       semicolons. A field that came as ONE comment stays one, so a title with
       a semicolon in it is never cut. */
    var VORBIS_JOIN = '; ';
    function vorbisKey(item) { var eq = item.indexOf('='); return eq > 0 ? item.substring(0, eq).toUpperCase() : ''; }
    /* Which comment keys the editor owns in THIS file: every canonical key, plus
       the aliases that stand in for an absent canonical one. → {KEY: field}. */
    function vorbisOwned(items) {
        var present = {}, owned = {}, f, i;
        for (i = 0; i < items.length; i++) present[vorbisKey(items[i])] = 1;
        for (f in VORBIS_MAP) owned[VORBIS_MAP[f]] = f;
        for (f in VORBIS_ALIASES) {
            if (present[VORBIS_MAP[f]]) continue;
            for (i = 0; i < VORBIS_ALIASES[f].length; i++) owned[VORBIS_ALIASES[f][i]] = f;
        }
        return owned;
    }
    /** The editor's fields as a comment list holds them: {vals: {field: [values]}, keys: {field: KEY}} —
        the values in file order, without empty ones and exact repeats; the key they stand under. */
    function vorbisRead(items) {
        var owned = vorbisOwned(items), vals = {}, keys = {};
        for (var i = 0; i < items.length; i++) {
            var key = vorbisKey(items[i]), f = owned[key];
            if (!f) continue;
            var v = items[i].substring(key.length + 1);
            if (!vals[f]) { vals[f] = []; keys[f] = key; }
            if (v.trim() !== '' && vals[f].indexOf(v) < 0) vals[f].push(v);
        }
        return { vals: vals, keys: keys };
    }
    /** Editor fields of a comment list; a field with several values reads as «a; b». */
    function vorbisFields(items) {
        var vals = vorbisRead(items).vals, tags = {};
        for (var f in vals) tags[f] = vals[f].join(VORBIS_JOIN);
        return tags;
    }
    /** How many editor fields of a comment list carry several values. */
    function vorbisMulti(items) {
        var vals = vorbisRead(items).vals, n = 0;
        for (var f in vals) if (vals[f].length > 1) n++;
        return n;
    }
    function vorbisSplit(text) {
        var parts = text.split(';'), out = [];
        for (var i = 0; i < parts.length; i++) { var s = parts[i].trim(); if (s && out.indexOf(s) < 0) out.push(s); }
        return out.length ? out : [text];
    }
    /** The comment list a save writes: foreign comments kept (unless `all`),
        the editor's fields from `set`. `dropPictures` — a picture carried as a
        comment goes too (the cover is being replaced or removed). */
    function vorbisItems(existing, set, all, dropPictures) {
        var items = [], owned = vorbisOwned(existing), had = vorbisRead(existing);
        if (!all) {
            for (var i = 0; i < existing.length; i++) {
                var key = vorbisKey(existing[i]);
                if (owned[key]) continue;                              // rewritten from `set` below
                if (VORBIS_PICTURE_KEYS[key] && dropPictures) continue;
                items.push(existing[i]);
            }
        }
        for (var f in VORBIS_MAP) {
            if (!set[f]) continue;
            var vals = [set[f]], old = had.vals[f], key = had.keys[f] || VORBIS_MAP[f];
            if (old && old.length > 1) vals = set[f] === old.join(VORBIS_JOIN).trim() ? old : vorbisSplit(set[f]);
            for (var j = 0; j < vals.length; j++) items.push(key + '=' + vals[j]);
        }
        return items;
    }

    function flacScan(u8) {
        var pos = 4, streaminfo = null, pics = [], vc = null, other = [], audioStart = u8.length;
        while (pos + 4 <= u8.length) {
            var header = u8[pos];
            var isLast = (header & 0x80) !== 0;
            var type = header & 0x7f;
            var len = (u8[pos + 1] << 16) | (u8[pos + 2] << 8) | u8[pos + 3];
            var body = u8.subarray(pos + 4, pos + 4 + len);
            if (type === 0) streaminfo = body.slice();
            else if (type === 6) pics.push(body.slice());
            else if (type === 4) { if (!vc) vc = body; }
            else if (type !== 1) other.push({ type: type, data: body });   // 1 = PADDING, not carried
            pos += 4 + len;
            if (isLast) { audioStart = pos; break; }
        }
        return { streaminfo: streaminfo, pics: pics, vc: vc, other: other, audioStart: audioStart };
    }
    function flacMetaHeader(type, len, last) {
        // The length field is 24 bits: a longer block would silently wrap and
        // every block after it would be read from the wrong place.
        if (len > 0xffffff) throw new Error('flac_block_too_large');
        return Uint8Array.of((last ? 0x80 : 0) | (type & 0x7f), (len >> 16) & 0xff, (len >> 8) & 0xff, len & 0xff);
    }
    function flacParseComments(blk) {
        var out = { vendor: '', items: [] };
        if (!blk || blk.length < 8) return out;
        try {
            var pos = 0, vlen = rdU32le(blk, pos); pos += 4;
            if (pos + vlen + 4 > blk.length) return out;
            out.vendor = utf8decode(blk.subarray(pos, pos + vlen)); pos += vlen;
            var n = rdU32le(blk, pos); pos += 4;
            for (var i = 0; i < n && pos + 4 <= blk.length; i++) {
                var l = rdU32le(blk, pos); pos += 4;
                if (pos + l > blk.length) break;
                out.items.push(utf8decode(blk.subarray(pos, pos + l))); pos += l;
            }
        } catch (_) {}
        return out;
    }
    function flacVorbisComment(vendorStr, items) {
        var vendor = utf8(vendorStr || '');
        var parts = [u32le(vendor.length), vendor, u32le(items.length)];
        for (var i = 0; i < items.length; i++) { var it = utf8(items[i]); parts.push(u32le(it.length), it); }
        return concat(parts);
    }
    function flacPictureBlock(mime, data) {
        var m = ascii(mime || 'image/jpeg');
        return concat([u32be(3), u32be(m.length), m, u32be(0), /* empty desc */
            u32be(0), u32be(0), u32be(0), u32be(0), u32be(data.length), data]);
    }
    /** Editor fields + vendor of a FLAC, by the same key rule the writer uses. */
    function readFlacComments(u8) {
        var cm = flacParseComments(flacScan(u8).vc);
        return { vendor: cm.vendor, tags: vorbisFields(cm.items), items: cm.items, multi: vorbisMulti(cm.items) };
    }
    function writeFlac(u8, ops) {
        var set = cleanSet(ops.set);
        var s = flacScan(u8);
        if (!s.streaminfo) throw new Error('flac_no_streaminfo');
        var audio = u8.subarray(s.audioStart);
        var all = !!ops.all;
        var old = flacParseComments(s.vc);

        /* The editor shows the vendor string as «encoder» when the file has no
           ENCODER comment of its own. Saved unchanged, it stays where it was —
           the vendor — instead of being copied into a new comment; cleared in
           the editor (or on a strip) it is gone. Nothing of OURS is ever
           written there (comment #1079: people byte-scan their outputs). */
        var hadEncoderComment = false, i;
        for (i = 0; i < old.items.length; i++) if (vorbisKey(old.items[i]) === 'ENCODER') { hadEncoderComment = true; break; }
        var vendor = (all || !set.encoder) ? '' : old.vendor;
        if (set.encoder && !hadEncoderComment && set.encoder === old.vendor) delete set.encoder;

        var vc = flacVorbisComment(vendor, vorbisItems(old.items, set, all, ops.cover !== undefined));

        // cover resolution
        var cover = ops.cover, picBlocks = [];
        if (cover === undefined) picBlocks = s.pics;                       // preserve raw PICTURE blocks
        else if (cover && cover.data && cover.data.length) picBlocks = [flacPictureBlock(cover.mime, cover.data)];
        // cover === null → no picture

        /* Everything that is not a tag stays: the seek table most of all, and
           on an edit also application blocks and a cue sheet. A strip keeps the
           seek table alone — the other two can carry names and ids. */
        var meta = [];
        for (i = 0; i < s.other.length; i++) {
            if (all && s.other[i].type !== 3) continue;
            meta.push({ type: s.other[i].type, data: s.other[i].data });
        }
        meta.push({ type: 4, data: vc });
        for (i = 0; i < picBlocks.length; i++) meta.push({ type: 6, data: picBlocks[i] });

        var blocks = [ascii('fLaC'), flacMetaHeader(0, s.streaminfo.length, false), s.streaminfo];
        for (i = 0; i < meta.length; i++) {
            blocks.push(flacMetaHeader(meta[i].type, meta[i].data.length, i === meta.length - 1), meta[i].data);
        }
        blocks.push(audio);
        return concat(blocks);
    }

    /* ════════════════════════════════════════════════════════════════════
       WAV — rewrite LIST/INFO and the `id3 ` chunk (text AND cover); every
       other chunk keeps its side of `data`; fix RIFF size.
       ════════════════════════════════════════════════════════════════════ */
    var WAV_INFO = { title: 'INAM', artist: 'IART', album: 'IPRD', year: 'ICRD', genre: 'IGNR', track: 'ITRK', comment: 'ICMT', copyright: 'ICOP', encoder: 'ISFT' };
    /* Four printable ASCII characters and a size that fits: a real chunk header.
       Used only to tell a genuinely empty `data` (size 0, more chunks follow)
       from a streaming writer's «length unknown» 0 (samples follow). */
    function riffChunkAt(u8, p) {
        if (p + 8 > u8.length) return false;
        for (var i = 0; i < 4; i++) { var c = u8[p + i]; if (c < 0x20 || c > 0x7e) return false; }
        return p + 8 + rdU32le(u8, p + 4) <= u8.length;
    }
    function writeWav(u8, ops) {
        var set = cleanSet(ops.set);
        /* Walk the chunks after the 12-byte RIFF/WAVE header. Old LIST(INFO) and
           id3 chunks are dropped — they are rewritten below. Every other chunk
           stays on its side of `data`: `cue `/`smpl`/`bext` that followed the
           samples still follow them, and trailing bytes that are not a chunk at
           all (an ID3v1 «TAG» someone appended) stay at the very end. Hoisted in
           front of `data`, their garbage «size» would send any reader walking
           the chunk list past the end of the file before it reached the samples. */
        var pos = 12, head = [], tail = [], dataParts = null, dataIntact = false, blockAlign = 0, picture = null, id3Last = null, infoKept = [];
        var infoOwned = {}; for (var wk in WAV_INFO) infoOwned[WAV_INFO[wk]] = 1;
        while (pos + 8 <= u8.length) {
            var id = String.fromCharCode(u8[pos], u8[pos + 1], u8[pos + 2], u8[pos + 3]);
            var size = rdU32le(u8, pos + 4);
            var total = 8 + size + (size & 1); // chunks are word-aligned
            var chunk = u8.subarray(pos, Math.min(pos + total, u8.length));
            var isListInfo = id === 'LIST' && u8[pos + 8] === 0x49 && u8[pos + 9] === 0x4E && u8[pos + 10] === 0x46 && u8[pos + 11] === 0x4F; // 'INFO'
            if (id === 'fmt ' && size >= 16 && pos + 22 <= u8.length) blockAlign = u8[pos + 20] | (u8[pos + 21] << 8);
            if (id === 'data' && !dataParts) {
                var have = u8.length - (pos + 8);
                var unknownLength = size === 0xFFFFFFFF || size > have || (size === 0 && have > 0 && !riffChunkAt(u8, pos + 8));
                if (!unknownLength) {
                    /* Rebuilt from the declared size with our own pad byte: a file
                       whose odd-sized `data` ends at EOF without one would
                       otherwise shift every tag chunk we append by a byte. */
                    dataParts = [u8.subarray(pos, pos + 8 + size)];
                    if (size & 1) dataParts.push(Uint8Array.of(0));
                    dataIntact = size > 0;
                } else {
                    /* ── РАЗМЕР `data` НЕ ПОМЕЩАЕТСЯ В ФАЙЛ — ЧИНИМ, А НЕ ОБХОДИМ ──
                       Так выглядит любой WAV, записанный в поток: FFmpeg в трубу
                       (`-f wav pipe:1`) ставит и RIFF, и `data` = 0xFFFFFFFF —
                       вернуться и дописать длину ему некуда. Ровно такой файл
                       прислал автор жалобы 2026-09-15 «Функция замены обложки не
                       работает» (заголовок `RIFF ff ff ff ff WAVE fmt `, 35 МБ).
                       Обрезанная загрузка выглядит так же, только число другое.

                       Раньше тег такому файлу клался ПЕРЕД `data`: читатель,
                       зажимающий длину по концу файла, съел бы хвостовой чанк
                       как сэмплы. Довод верный, но его цена — слепая зона
                       Проводника. Замер 2026-09-15 на потоковом WAV из FFmpeg:
                       тег перед `data` — название и исполнитель в свойствах
                       файла ПУСТЫ; тот же тег за `data` после починки размера —
                       видны. Проигрывание не меняется ни у кого: Media
                       Foundation и ffprobe дают те же 6.000 с до и после.

                       Размер = сэмплы, которые РЕАЛЬНО лежат в файле, целыми
                       кадрами (blockAlign). Хвост короче кадра — полкадра,
                       которые не декодирует никто. После этого `data` цел, и
                       тег идёт за ним, как у всех. */
                    if (blockAlign > 1) have -= have % blockAlign;
                    dataParts = [ascii('data'), u32le(have), u8.subarray(pos + 8, pos + 8 + have)];
                    if (have & 1) dataParts.push(Uint8Array.of(0));
                    dataIntact = have > 0;
                    break;                                   // the samples run to the end of the file
                }
            }
            else if (id.toLowerCase() === 'id3 ') {
                /* Keep the picture the reader SHOWS: parseWAV lets a later id3
                   chunk override an earlier one, so the last picture found wins. */
                var id3Old = u8.subarray(pos + 8, Math.min(pos + 8 + size, u8.length));
                if (id3Old.length > 10) {
                    var pic = mp3ExtractApic(id3Old, Math.min(10 + rdSynchsafe(id3Old, 6), id3Old.length));
                    if (pic) picture = pic;
                    id3Last = id3Old;                 // the reader lets a later chunk win — so does the carry
                }
            }
            else if (isListInfo) {
                /* INFO fields the editor does not show (IENG, ISRC — the source
                   medium, IKEY…) stay on an edit; a strip takes them. */
                var lp = pos + 12, lend = Math.min(pos + 8 + size, u8.length);
                while (lp + 8 <= lend) {
                    var sid = String.fromCharCode(u8[lp], u8[lp + 1], u8[lp + 2], u8[lp + 3]), ss = rdU32le(u8, lp + 4);
                    var stotal = 8 + ss + (ss & 1);
                    if (lp + 8 + ss > lend) break;
                    if (!infoOwned[sid]) { var sub = u8.subarray(lp, Math.min(lp + stotal, lend)); infoKept.push((sub.length & 1) ? concat([sub, Uint8Array.of(0)]) : sub); }
                    lp += stotal;
                }
            }
            else (dataParts ? tail : head).push(chunk);
            pos += total;
        }
        /* ── LIST/INFO ПРИНИМАЕТ ТОЛЬКО ASCII ──────────────────────────────
           Здесь стояло `utf8(set[key])` для ЛЮБОГО значения. У RIFF
           `LIST/INFO` объявления кодировки нет вообще (спецификация старше
           Unicode), и всякий плеер на Windows читает эти байты системной
           ANSI-страницей. То есть кириллический заголовок уезжал сюда
           гарантированной кашей: `С`→d0a1→«РЎ», `ц`→d186→«С†», `а`→d0b0→«Р°»
           — ровно то, на что жаловался #3792 («У с галочкой, кресты,
           градусы»).

           ⚠️⚠️ И этого НЕ ВИДЕЛ собственный тест: его оракул — ffprobe, а
           ffmpeg читает INFO как UTF-8, то есть разделяет наше же неверное
           допущение. «Unicode title round-trips» был зелёным ровно тогда,
           когда пользователь видел кашу. Поэтому проверка ниже смотрит на
           БАЙТЫ чанка, а не спрашивает ffprobe.

           Не-ASCII теперь уходит в чанк `id3 ` — единственное место в WAV,
           где кодировка ОБЪЯВЛЕНА (ID3v2.3: 0x00 latin-1 / 0x01 UTF-16+BOM).
           Его читают TagLib (VLC, Kid3), mutagen (Picard) и ffmpeg — замер
           2026-09-15, — и, по их документации, foobar2000 и Mp3tag. Проводник
           Windows не читает его НИКАК — ни текст, ни обложку (тот же замер):
           там остаётся имя файла, которое лежит в ФС в Unicode и верно.
           Пустое поле честнее сломанного. */
        var infoItems = [];
        for (var key in WAV_INFO) {
            if (!set[key]) continue;
            if (!isLatin1Ascii(set[key])) continue;               // ушло в `id3 `
            var body = concat([ascii(set[key]), Uint8Array.of(0)]); // null-terminated
            if (body.length & 1) body = concat([body, Uint8Array.of(0)]);
            infoItems.push(concat([ascii(WAV_INFO[key]), u32le(body.length), body]));
        }
        if (!ops.all) infoItems = infoItems.concat(infoKept);
        var listChunk = null;
        if (infoItems.length) {
            var infoBody = concat([ascii('INFO')].concat(infoItems));
            listChunk = concat([ascii('LIST'), u32le(infoBody.length), infoBody]);
        }
        /* Чанк `id3 ` — полный набор с ОБЪЯВЛЕННОЙ кодировкой. Пишем его
           всегда, когда есть что писать: для ASCII он дублирует INFO (плееры
           читают любой из двух и получают одно и то же), для не-ASCII он
           единственный носитель. Размер чанка нечётный → RIFF требует
           дополнения до чётной границы.

           ── И ОБЛОЖКА — В ТОМ ЖЕ ЧАНКЕ ──────────────────────────────────────
           До 2026-09-15 здесь писался только текст, в движке стояло
           `COVER_CAPABLE.wav = false`, и тул отвечал человеку «этот формат не
           поддерживает встроенную обложку». Это неправда: у WAV есть
           де-факто стандарт — тот же кадр APIC в том же чанке `id3 `, куда мы
           уже кладём текст. Хуже того, обход выше выбрасывал старый `id3 `
           целиком, и обложка, которую файл УЖЕ нёс, молча пропадала при
           правке одного названия (RU, 10 дней: 14 таких WAV в 9 сессиях).

           Замер 2026-09-15: одна и та же обложка, менялись только регистр
           (`id3 ` / `ID3 `) и место чанка (до `fmt `, между, после `data`).
             · TagLib (VLC, Kid3, Mixxx), mutagen (Picard, beets), ffprobe —
               видят обложку ВО ВСЕХ вариантах;
             · Проводник Windows (миниатюра, IShellItemImageFactory) — не
               видит НИ В ОДНОМ, при 100 % у контрольных MP3/FLAC/M4A.
           Отсюда: `id3 ` строчными (foobar2000 и JRiver, по справке Kid3,
           читают только его), за `data` (так пишут TagLib, mutagen и
           Audacity), а про Проводник тул говорит сам — в блоке обложки.
           Пустой `id3 ` не пишем никогда: foobar2000 объявлял такой WAV
           повреждённым (mutagen #392).

           cover: undefined = оставить картинку файла, null = убрать,
           {data,mime} = заменить — тот же контракт, что у writeMp3. */
        var cover = ops.cover === undefined ? picture : ops.cover;
        var walkW = id3Last ? id3Walk(id3Last) : null;
        var id3Frames = id3TextFrames(set, id3Langs(walkW));
        if (walkW && !ops.all) id3Frames = id3Frames.concat(id3Carry(walkW, ops.cover));
        if (cover && cover.data && cover.data.length) id3Frames.push(id3ApicFrame(cover.mime, cover.data));
        var id3Chunk = null;
        if (id3Frames.length) {
            var id3Body = id3v23Tag(id3Frames);
            var pad = (id3Body.length & 1) ? Uint8Array.of(0) : new Uint8Array(0);
            id3Chunk = concat([ascii('id3 '), u32le(id3Body.length), id3Body, pad]);
        }
        /* ── ГДЕ ЛЕЖИТ ЧАНК — НЕ КОСМЕТИКА ─────────────────────────────────
           Здесь тег клался МЕЖДУ `fmt ` и `data`. Так пишет и ffmpeg, так
           написано во всех примерах — и ровно этого места Проводник Windows
           НЕ ВИДИТ.

           Замер 2026-09-07, Windows 11 (26200), обработчик свойств WAV —
           `mfsrcsnk.dll`, спрошен через IPropertyStore, то есть тем же путём,
           каким рисуется вкладка «Подробно»; байты INFO во всех случаях
           одинаковые, менялась только позиция чанка:

             LIST/INFO  до `fmt `           → читает  ✓
             LIST/INFO  после `data`        → читает  ✓
             LIST/INFO  между `fmt` и `data`→ ПУСТО   ✗   (и наш, и ffmpeg'а)

           Поэтому в свойствах WAV не показывалось НИ ОДНО поле — даже чисто
           латинское, даже у файла, сделанного самим ffmpeg. Жалоба #3765:
           «ни название, ни исполнитель, ни авторские права». Это была не
           кодировка: пустым было всё.

           Кладём после `data` (а не перед `fmt `): перед `fmt ` тег читают
           те же, но наивный парсер, который ждёт `fmt ` первым чанком, там
           спотыкается. Проверено, что после `data` тег видят ffmpeg, наш
           собственный parseWAV и Проводник; поток аудио побитово тот же. */
        var tagChunks = [];
        if (listChunk) tagChunks.push(listChunk);
        if (id3Chunk) tagChunks.push(id3Chunk);
        var out = [ascii('RIFF'), u32le(0), ascii('WAVE')].concat(head,
            dataIntact ? [] : tagChunks,
            dataParts || [],
            dataIntact ? tagChunks : [],
            tail);
        var body2 = concat(out);
        // fix RIFF size = filelen - 8
        var riffSize = body2.length - 8;
        body2[4] = riffSize & 0xff; body2[5] = (riffSize >>> 8) & 0xff; body2[6] = (riffSize >>> 16) & 0xff; body2[7] = (riffSize >>> 24) & 0xff;
        return body2;
    }

    /* ════════════════════════════════════════════════════════════════════
       riffTagsAfterDataParts — ТО ЖЕ ПРАВИЛО ПОЗИЦИИ, но для чужой выдачи.

       writeWav кладёт тег за `data` сам. Однако WAV у нас рождается и вторым
       путём — из FFmpeg (там, где тул перекодирует, а не правит теги), а
       FFmpeg кладёт `LIST/INFO` МЕЖДУ `fmt ` и `data`, то есть ровно туда,
       где Проводник Windows его не видит (замер — в шапке writeWav). Значит
       у /extract-audio, где человек своими руками печатает исполнителя и
       альбом рядом с выбором «WAV», тег так же не доезжал до свойств файла.

       Правило про позицию обязано жить в ОДНОМ месте, иначе две копии молча
       разойдутся, — поэтому door FFmpeg зовёт эту функцию, а не повторяет
       обход чанков у себя.

       Возвращает СПИСОК КУСКОВ (подмассивы исходных байтов) для `new Blob(...)`,
       а не новый буфер: экспорт WAV бывает в сотни мегабайт, и лишняя полная
       копия — это тот же класс OOM, с которым тут борются везде. `null`
       означает «делать нечего» — тогда вызывающий не платит вообще ничего.

       Не трогаем и возвращаем null, если:
         · это не RIFF/WAVE, нет `data`, или тег уже за `data`;
         · объявленный размер `data` не помещается в файл (обрезанная или
           потоковая запись: читатель, зажимающий длину по концу файла, съел
           бы хвостовой чанк как сэмплы — это треск вместо тега);
         · в INFO нет ничего, кроме `ISFT` (подпись кодировщика). Такой тег
           человеку не виден, а перекладывание стоило бы прохода по файлу
           на КАЖДОМ экспорте WAV.
       ════════════════════════════════════════════════════════════════════ */
    function riffTagsAfterDataParts(u8) {
        if (!u8 || u8.length < 44) return null;
        if (!(u8[0] === 0x52 && u8[1] === 0x49 && u8[2] === 0x46 && u8[3] === 0x46)) return null; // 'RIFF'
        if (!(u8[8] === 0x57 && u8[9] === 0x41 && u8[10] === 0x56 && u8[11] === 0x45)) return null; // 'WAVE'
        var pos = 12, head = [], tail = [], dataChunk = null, dataIntact = false, moved = 0;
        while (pos + 8 <= u8.length) {
            var id = String.fromCharCode(u8[pos], u8[pos + 1], u8[pos + 2], u8[pos + 3]);
            var size = rdU32le(u8, pos + 4);
            var total = 8 + size + (size & 1);
            if (total < 8 || total > u8.length - pos + 8) break;         // битый обход — не рискуем
            var chunk = u8.subarray(pos, Math.min(pos + total, u8.length));
            var isListInfo = id === 'LIST' && u8[pos + 8] === 0x49 && u8[pos + 9] === 0x4E && u8[pos + 10] === 0x46 && u8[pos + 11] === 0x4F;
            var isId3 = id.toLowerCase() === 'id3 ';
            if (id === 'data') {
                dataChunk = chunk;
                dataIntact = size > 0 && pos + 8 + size <= u8.length;
            } else if ((isListInfo || isId3) && !dataChunk) {
                // тег ПЕРЕД data — только его и надо перекладывать
                if (isListInfo && !infoHasUserField(u8, pos + 12, pos + 8 + size)) head.push(chunk);
                else { tail.push(chunk); moved++; }
            } else {
                (dataChunk ? tail : head).push(chunk);
            }
            pos += total;
        }
        if (!moved || !dataChunk || !dataIntact) return null;
        return [u8.subarray(0, 12)].concat(head, [dataChunk], tail);
    }
    /* Есть ли в списке INFO хоть одно поле, кроме подписи кодировщика. */
    function infoHasUserField(u8, from, to) {
        var lp = from;
        while (lp + 8 <= to) {
            var sid = String.fromCharCode(u8[lp], u8[lp + 1], u8[lp + 2], u8[lp + 3]);
            var ss = rdU32le(u8, lp + 4);
            if (sid !== 'ISFT' && ss > 0) return true;
            lp += 8 + ss + (ss & 1);
            if (ss < 0) break;
        }
        return false;
    }

    /* ════════════════════════════════════════════════════════════════════
       M4A / MP4 — iTunes metadata lives in moov/udta/meta(hdlr 'mdir')/ilst.
       Higher-risk atom surgery; guarded by WRITABLE.m4a (tests are the judge).

       Rewritten 2026-09-25 after «Не прикрепилась обложка к видео» (Android
       camera MP4, 277 MB): the old writer shipped three defects, none visible
       on ffmpeg-made fixtures, which always carry an iTunes `meta`:
       1. A file WITHOUT its own iTunes meta (Android MPEG4Writer, MediaRecorder,
          most cameras and voice recorders) got a `hdlr` with no size field, so
          no reader could walk past it: title, artist AND cover were written and
          unreadable everywhere — ffprobe, Windows Explorer, this page. Covers
          were caught by the page's read-back (26 `cover_ok:0` saves / 16
          sessions since 2026-09-15); text edits were not checked at all.
       2. The whole old `udta` was thrown away — the GPS point of a phone video
          (`©xyz`) and Nero chapters (`chpl`) vanished on a title edit.
       3. Chunk offsets were patched by a byte scan for the letters «stco» —
          fragmented MP4 (absolute `tfhd` base offsets) came back with every
          fragment pointing into the wrong bytes («Invalid NAL unit size»),
          and a cover JPEG that happens to contain «stco» would have been
          «patched» too.
       Now the tree is walked, every absolute offset kind that can point past
       the moov is moved (stco/co64, tfhd base_data_offset, tfra, sidx in
       front of moov), anything this writer cannot move is refused (so the
       page takes its FFmpeg lane), and the result proves itself: the media
       bytes behind every offset are compared with the original before the
       file is handed out.
       ════════════════════════════════════════════════════════════════════ */
    function a4(str) { return Uint8Array.of(str.charCodeAt(0), str.charCodeAt(1), str.charCodeAt(2), str.charCodeAt(3)); }
    function atom(type4, body) { return concat([u32be(8 + body.length), type4, body]); } // type4: Uint8Array(4)
    function rdU64be(u8, o) { return rdU32be(u8, o) * 0x100000000 + rdU32be(u8, o + 4); }
    function wrU32be(u8, o, v) { u8[o] = (v >>> 24) & 0xff; u8[o + 1] = (v >>> 16) & 0xff; u8[o + 2] = (v >>> 8) & 0xff; u8[o + 3] = v & 0xff; }
    function wrU64be(u8, o, v) { wrU32be(u8, o, Math.floor(v / 0x100000000)); wrU32be(u8, o + 4, v >>> 0); }
    var MP4_TEXT = {
        title: [0xA9, 0x6E, 0x61, 0x6D], artist: [0xA9, 0x41, 0x52, 0x54], album: [0xA9, 0x61, 0x6C, 0x62],
        albumArtist: [0x61, 0x41, 0x52, 0x54], year: [0xA9, 0x64, 0x61, 0x79], genre: [0xA9, 0x67, 0x65, 0x6E],
        composer: [0xA9, 0x77, 0x72, 0x74], copyright: [0x63, 0x70, 0x72, 0x74], comment: [0xA9, 0x63, 0x6D, 0x74],
        encoder: [0xA9, 0x74, 0x6F, 0x6F], lyrics: [0xA9, 0x6C, 0x79, 0x72]
    };
    function mp4DataAtom(typeFlag, value) {
        var body = concat([u32be(typeFlag), u32be(0), value]); // type indicator + locale(0) + value
        return atom(a4('data'), body);
    }
    function mp4TextTag(name4bytes, str) {
        return atom(new Uint8Array(name4bytes), mp4DataAtom(1 /*UTF-8*/, utf8(str)));
    }
    function mp4TrknTag(name4bytes, num, total) {
        var v = new Uint8Array(8);
        v[2] = (num >> 8) & 0xff; v[3] = num & 0xff; v[4] = (total >> 8) & 0xff; v[5] = total & 0xff;
        return atom(new Uint8Array(name4bytes), mp4DataAtom(0, v));
    }
    function mp4CoverTag(mime, data) {
        var flag = /png/i.test(mime) ? 14 : 13; // 13=JPEG, 14=PNG
        return atom(a4('covr'), mp4DataAtom(flag, data));
    }
    // Parse child atoms in [start,end). Returns [{type, start, hdr, dataStart, end}].
    // A box shorter than its own header ends the walk; a box running past the
    // buffer (a truncated mdat) is clamped, and the caller keeps any tail bytes.
    function mp4Children(u8, start, end) {
        var list = [], pos = start;
        while (pos + 8 <= end) {
            var size = rdU32be(u8, pos), hdr = 8;
            if (size === 1) { if (pos + 16 > end) break; size = rdU64be(u8, pos + 8); hdr = 16; }
            else if (size === 0) size = end - pos;
            if (size < hdr) break;
            var type = String.fromCharCode(u8[pos + 4], u8[pos + 5], u8[pos + 6], u8[pos + 7]);
            list.push({ type: type, start: pos, hdr: hdr, dataStart: pos + hdr, end: Math.min(pos + size, end) });
            pos += size;
        }
        return list;
    }
    function findChild(children, type) { for (var i = 0; i < children.length; i++) if (children[i].type === type) return children[i]; return null; }
    function isFourcc(u8, o, s) { return u8[o] === s.charCodeAt(0) && u8[o + 1] === s.charCodeAt(1) && u8[o + 2] === s.charCodeAt(2) && u8[o + 3] === s.charCodeAt(3); }

    // The iTunes `hdlr` — a COMPLETE atom, size field included (the missing
    // size was defect 1 above). Byte-for-byte what ffmpeg and iTunes write.
    var HDLR_MDIR = atom(a4('hdlr'), concat([
        u32be(0), /* version+flags */ u32be(0), /* pre_defined */
        a4('mdir'), a4('appl'), u32be(0), u32be(0), Uint8Array.of(0)
    ]));
    /* `meta` exists in two shapes: iTunes/ISO puts 4 bytes of version/flags
       before its children, QuickTime's own moov-level `meta` (Android and Apple
       device keys: com.android.version, location …) has none. Where the first
       child's type sits tells them apart. */
    function metaChildrenStart(u8, meta) {
        if (isFourcc(u8, meta.dataStart + 4, 'hdlr')) return meta.dataStart;      // QuickTime
        return meta.dataStart + 4;                                                // ISO FullBox
    }
    function metaHandler(u8, meta) {
        var h = findChild(mp4Children(u8, metaChildrenStart(u8, meta), meta.end), 'hdlr');
        if (!h || h.dataStart + 12 > h.end) return null;
        return String.fromCharCode(u8[h.dataStart + 8], u8[h.dataStart + 9], u8[h.dataStart + 10], u8[h.dataStart + 11]);
    }

    /* An EDIT keeps the ilst atoms the editor does not show — tempo (tmpo),
       compilation and gapless flags, sort names, grouping, ratings, the
       freeform «----» atoms (iTunNORM, MusicBrainz ids); a STRIP (all) drops
       them. gnre is the genre as an ID3v1 number: once a genre is written
       under ©gen it would only contradict it. */
    var MP4_OWNED = { trkn: 1, disk: 1, covr: 1 };
    (function () { for (var k in MP4_TEXT) MP4_OWNED[String.fromCharCode.apply(null, MP4_TEXT[k])] = 1; })();
    function buildIlst(set, cover, existingIlst, u8, all) {
        var tags = [];
        for (var key in MP4_TEXT) { if (set[key]) tags.push(mp4TextTag(MP4_TEXT[key], set[key])); }
        if (set.track) { var t = String(set.track).split('/'); tags.push(mp4TrknTag([0x74, 0x72, 0x6B, 0x6E], parseInt(t[0], 10) || 0, parseInt(t[1], 10) || 0)); }
        if (set.disc) { var d = String(set.disc).split('/'); tags.push(mp4TrknTag([0x64, 0x69, 0x73, 0x6B], parseInt(d[0], 10) || 0, parseInt(d[1], 10) || 0)); }
        // cover
        if (cover === undefined && existingIlst) {
            var kids = mp4Children(u8, existingIlst.dataStart, existingIlst.end);
            var c = findChild(kids, 'covr');
            if (c) tags.push(u8.subarray(c.start, c.end).slice());
        } else if (cover && cover.data && cover.data.length) {
            tags.push(mp4CoverTag(cover.mime, cover.data));
        }
        if (!all && existingIlst) {
            var other = mp4Children(u8, existingIlst.dataStart, existingIlst.end);
            for (var o = 0; o < other.length; o++) {
                if (MP4_OWNED[other[o].type]) continue;
                if (other[o].type === 'gnre' && set.genre) continue;
                tags.push(u8.subarray(other[o].start, other[o].end));
            }
        }
        return atom(a4('ilst'), concat(tags));
    }

    /* ── absolute offsets ─────────────────────────────────────────────────
       Everything that sat after the old moov now sits `delta` further on.
       Each walker returns a list of {at, old, wide} records — where in the NEW
       bytes an offset field lives and what it held — so the self-check below
       can compare the media behind it with the original. */
    function eachStbl(u8, start, end, fn) {
        var kids = mp4Children(u8, start, end);
        for (var i = 0; i < kids.length; i++) {
            var k = kids[i];
            if (k.type === 'trak' || k.type === 'mdia' || k.type === 'minf') eachStbl(u8, k.dataStart, k.end, fn);
            else if (k.type === 'stbl') fn(k);
        }
    }
    // stco/co64 inside a moov that starts at `base` of `buf`.
    function shiftChunkOffsets(buf, moovAt, oldMoovEnd, delta, recs) {
        var moov = mp4Children(buf, moovAt, buf.length)[0];
        eachStbl(buf, moov.dataStart, moov.end, function (stbl) {
            var kids = mp4Children(buf, stbl.dataStart, stbl.end);
            for (var i = 0; i < kids.length; i++) {
                var k = kids[i], wide = k.type === 'co64';
                if (k.type === 'saio' && delta) throw new Error('m4a_absolute_aux_offsets'); // CENC in moov: absolute, not ours to move
                if (k.type !== 'stco' && !wide) continue;
                var n = rdU32be(buf, k.dataStart + 4), step = wide ? 8 : 4;
                if (k.dataStart + 8 + n * step > k.end) throw new Error('m4a_bad_chunk_table');
                for (var j = 0; j < n; j++) {
                    var o = k.dataStart + 8 + j * step;
                    var v = wide ? rdU64be(buf, o) : rdU32be(buf, o);
                    if (v >= oldMoovEnd && delta) {
                        var nv = v + delta;
                        if (!wide && nv > 0xFFFFFFFF) throw new Error('m4a_stco_overflow');
                        if (wide) wrU64be(buf, o, nv); else wrU32be(buf, o, nv);
                    }
                    recs.push({ old: v, now: (v >= oldMoovEnd ? v + delta : v) });
                }
            }
        });
    }
    // Fragmented MP4: moof/traf/tfhd base_data_offset (flag 0x000001) is an
    // absolute file position; mfra/tfra keeps absolute moof positions; a sidx
    // placed IN FRONT of moov measures the distance across it.
    function shiftFragmentOffsets(buf, oldMoovStart, oldMoovEnd, delta, recs) {
        var top = mp4Children(buf, 0, buf.length);
        for (var i = 0; i < top.length; i++) {
            var b = top[i];
            if (b.type === 'moof') {
                var trafs = mp4Children(buf, b.dataStart, b.end);
                for (var t = 0; t < trafs.length; t++) {
                    if (trafs[t].type !== 'traf') continue;
                    var tfhd = findChild(mp4Children(buf, trafs[t].dataStart, trafs[t].end), 'tfhd');
                    if (!tfhd) continue;
                    var flags = (buf[tfhd.dataStart + 1] << 16) | (buf[tfhd.dataStart + 2] << 8) | buf[tfhd.dataStart + 3];
                    if (!(flags & 1)) continue; // relative to the moof itself: moves with it
                    var o = tfhd.dataStart + 8, v = rdU64be(buf, o);
                    if (v >= oldMoovEnd) { wrU64be(buf, o, v + delta); recs.push({ old: v, now: v + delta }); }
                    else recs.push({ old: v, now: v });
                }
            } else if (b.type === 'mfra') {
                var tfras = mp4Children(buf, b.dataStart, b.end);
                for (var f = 0; f < tfras.length; f++) {
                    if (tfras[f].type !== 'tfra') continue;
                    var p = tfras[f].dataStart, ver = buf[p];
                    var sizes = rdU32be(buf, p + 8), n = rdU32be(buf, p + 12);
                    var extra = (((sizes >> 4) & 3) + 1) + (((sizes >> 2) & 3) + 1) + ((sizes & 3) + 1);
                    var e = p + 16;
                    for (var j = 0; j < n; j++) {
                        if (ver === 1) {
                            var mo = rdU64be(buf, e + 8);
                            if (mo >= oldMoovEnd) wrU64be(buf, e + 8, mo + delta);
                            e += 16 + extra;
                        } else {
                            var mo32 = rdU32be(buf, e + 4);
                            if (mo32 >= oldMoovEnd) wrU32be(buf, e + 4, mo32 + delta);
                            e += 8 + extra;
                        }
                    }
                }
            } else if (b.type === 'sidx' && b.start < oldMoovStart) {
                // FullBox(4) + reference_ID(4) + timescale(4) + EPT(4|8) → first_offset
                var sv = buf[b.dataStart], fo = b.dataStart + (sv === 1 ? 20 : 16);
                if (sv === 1) wrU64be(buf, fo, rdU64be(buf, fo) + delta); else wrU32be(buf, fo, rdU32be(buf, fo) + delta);
            } else if (b.type === 'meta' && findChild(mp4Children(buf, metaChildrenStart(buf, b), b.end), 'iloc')) {
                throw new Error('m4a_item_locations'); // HEIF-style item offsets: not ours to move
            }
        }
    }
    // The file proves itself before it leaves: behind every offset the media
    // bytes must be the ones that were there before the rewrite.
    function verifyOffsets(src, out, recs) {
        for (var i = 0; i < recs.length; i++) {
            var r = recs[i];
            if (r.old + 16 > src.length) continue; // offset already past a truncated file
            if (r.now + 16 > out.length) throw new Error('m4a_verify_oob');
            for (var k = 0; k < 16; k++) if (out[r.now + k] !== src[r.old + k]) throw new Error('m4a_verify_mismatch');
        }
    }
    function mp4HasVideo(u8, moov) {
        var found = false;
        var kids = mp4Children(u8, moov.dataStart, moov.end);
        for (var i = 0; i < kids.length && !found; i++) {
            if (kids[i].type !== 'trak') continue;
            var mdia = findChild(mp4Children(u8, kids[i].dataStart, kids[i].end), 'mdia');
            var hdlr = mdia && findChild(mp4Children(u8, mdia.dataStart, mdia.end), 'hdlr');
            if (hdlr && isFourcc(u8, hdlr.dataStart + 8, 'vide')) found = true;
        }
        return found;
    }

    function writeM4a(u8, ops) {
        var set = cleanSet(ops.set);
        var top = mp4Children(u8, 0, u8.length);
        var moov = findChild(top, 'moov');
        if (!moov) throw new Error('m4a_no_moov');

        var moovKids = mp4Children(u8, moov.dataStart, moov.end);
        var udta = findChild(moovKids, 'udta');
        var udtaKids = udta ? mp4Children(u8, udta.dataStart, udta.end) : [];
        var itMeta = null;
        for (var i = 0; i < udtaKids.length; i++) {
            if (udtaKids[i].type !== 'meta') continue;
            var h = metaHandler(u8, udtaKids[i]);
            if (h !== null && h !== 'mdir') throw new Error('m4a_foreign_meta:' + h); // not iTunes — FFmpeg lane decides
            if (itMeta) throw new Error('m4a_two_meta');
            itMeta = udtaKids[i];
        }
        var ilst = null;
        if (itMeta) ilst = findChild(mp4Children(u8, metaChildrenStart(u8, itMeta), itMeta.end), 'ilst');

        var newIlst = buildIlst(set, ops.cover, ilst, u8, !!ops.all);
        var newMeta = atom(a4('meta'), concat([u32be(0) /* version/flags */, HDLR_MDIR, newIlst]));

        // An EDIT keeps everything else the udta carried (GPS `©xyz`, chapters…);
        // STRIP (ops.all) is «remove all metadata» and takes all of it.
        var udtaParts = [];
        if (!ops.all) for (var u = 0; u < udtaKids.length; u++) if (udtaKids[u] !== itMeta) udtaParts.push(u8.subarray(udtaKids[u].start, udtaKids[u].end));
        if (!ops.all || newIlst.length > 8) udtaParts.push(newMeta);
        var newUdta = udtaParts.length ? atom(a4('udta'), concat(udtaParts)) : null;

        var moovParts = [], placed = false;
        for (var m = 0; m < moovKids.length; m++) {
            var kid = moovKids[m];
            if (kid.type === 'udta') { if (newUdta && !placed) { moovParts.push(newUdta); placed = true; } continue; }
            // moov-level QuickTime keys (device, capture fps, sometimes location): strip removes them too
            if (ops.all && kid.type === 'meta') continue;
            moovParts.push(u8.subarray(kid.start, kid.end));
        }
        if (newUdta && !placed) moovParts.push(newUdta);
        var newMoov = atom(a4('moov'), concat(moovParts));
        var delta = newMoov.length - (moov.end - moov.start);

        // Reassemble; bytes after the last box a parser can see (a damaged
        // tail) are kept as they were rather than silently cut off.
        var out = [], newMoovAt = 0, pos = 0, seenMoov = false;
        for (var t = 0; t < top.length; t++) {
            if (top[t].type === 'moov') { newMoovAt = pos; seenMoov = true; out.push(newMoov); pos += newMoov.length; }
            else { var part = u8.subarray(top[t].start, top[t].end); out.push(part); pos += part.length; }
        }
        var lastEnd = top.length ? top[top.length - 1].end : 0;
        if (lastEnd < u8.length) out.push(u8.subarray(lastEnd));
        var result = concat(out);

        var recs = [];
        shiftChunkOffsets(result, newMoovAt, moov.end, delta, recs);
        if (delta) shiftFragmentOffsets(result, moov.start, moov.end, delta, recs);
        verifyOffsets(u8, result, recs);
        return result;
    }
    function m4aMime(u8) {
        return hasVideo(u8) ? 'video/mp4' : 'audio/mp4';
    }
    // true/false for an ISO-BMFF file (a `vide` track that is not a cover);
    // null when the bytes are not ISO-BMFF and the caller must judge by type.
    function hasVideo(u8) {
        try {
            if (!(u8 && u8.length > 8 && isFourcc(u8, 4, 'ftyp'))) return null;
            var moov = findChild(mp4Children(u8, 0, u8.length), 'moov');
            return moov ? mp4HasVideo(u8, moov) : null;
        } catch (e) { return null; }
    }

    /* ════════════════════════════════════════════════════════════════════
       OGG (Vorbis / Opus) — rewrite the comment header packet; every audio
       page is copied byte-for-byte and only its sequence number + CRC change.

       WHY (2026-09-05, /audio-metadata, RU 30 d): OGG was the ONE format the
       blade still routed through FFmpeg-WASM, and that lane produced the
       tool's largest error cluster — «Unsupported codec id in stream 1 /
       Could not write header» ×38, plus «ffmpeg-exit» ×29 and the engine
       failing to load at all. The cause is structural, not a bug to patch:
       FFmpeg takes a cover as a VIDEO STREAM, and the Ogg muxer has no such
       thing. The Vorbis/Opus way to carry a picture is a comment field,
       METADATA_BLOCK_PICTURE = base64(FLAC PICTURE block) — text, inside the
       comment header, no second stream. That is what this writes.

       Layout contract (Ogg + Vorbis I / RFC 7845):
         page 0            identification packet, alone  — kept VERBATIM
         page 1..k         comment packet (+ Vorbis setup packet), granule 0
         page k+1..        audio; the first audio packet starts a fresh page
       So the rewrite is: keep page 0, re-paginate the header packets after
       it, then renumber + re-CRC the untouched audio pages. Anything outside
       that contract (chained/multiplexed streams, an audio packet sharing the
       last header page) throws, and the blade falls back to FFmpeg as before.

       Unknown comment fields (REPLAYGAIN_*, MUSICBRAINZ_*, …) are PRESERVED on
       edit: an editor that silently drops what it does not display is the
       same defect as one that adds its own fingerprint. Strip drops all.
       ════════════════════════════════════════════════════════════════════ */
    // Ogg page CRC: polynomial 0x04c11db7, MSB-first, init 0, no reflection,
    // no final XOR — computed over the page with its CRC field zeroed.
    var OGG_CRC = (function () {
        var t = new Uint32Array(256);
        for (var i = 0; i < 256; i++) {
            var r = (i << 24) >>> 0;
            for (var j = 0; j < 8; j++) r = ((r & 0x80000000) ? ((r << 1) ^ 0x04c11db7) : (r << 1)) >>> 0;
            t[i] = r;
        }
        return t;
    })();
    function oggCrc(u8, start, end) {
        var c = 0;
        for (var i = start; i < end; i++) c = (((c << 8) >>> 0) ^ OGG_CRC[((c >>> 24) ^ u8[i]) & 0xff]) >>> 0;
        return c >>> 0;
    }
    function oggPages(u8) {
        var pages = [], pos = 0;
        while (pos + 27 <= u8.length) {
            if (!(u8[pos] === 0x4F && u8[pos + 1] === 0x67 && u8[pos + 2] === 0x67 && u8[pos + 3] === 0x53)) throw new Error('ogg_bad_page');
            var nseg = u8[pos + 26], hdrLen = 27 + nseg;
            if (pos + hdrLen > u8.length) throw new Error('ogg_truncated');
            var segs = u8.subarray(pos + 27, pos + hdrLen), dataLen = 0;
            for (var i = 0; i < nseg; i++) dataLen += segs[i];
            var p = { start: pos, headerType: u8[pos + 5], serial: rdU32le(u8, pos + 14), seq: rdU32le(u8, pos + 18),
                      crc: rdU32le(u8, pos + 22), segs: segs, dataStart: pos + hdrLen, end: pos + hdrLen + dataLen };
            if (p.end > u8.length) throw new Error('ogg_truncated');
            pages.push(p);
            pos = p.end;
        }
        if (!pages.length) throw new Error('ogg_no_pages');
        return pages;
    }
    // The first `need` logical packets, reassembled across pages. Returns the
    // packets, the index of the page the last one ends on, and demands that
    // page 0 holds the first packet alone and that no audio begins on the last
    // header page — the two layout facts the rewrite relies on.
    function oggHeaderPackets(u8, pages, need) {
        var packets = [], cur = [], curLen = 0, lastPage = -1;
        for (var pi = 0; pi < pages.length; pi++) {
            var p = pages[pi], off = p.dataStart;
            if (pi > 0 && p.serial !== pages[0].serial) throw new Error('ogg_multistream');
            for (var si = 0; si < p.segs.length; si++) {
                var len = p.segs[si];
                if (len) { cur.push(u8.subarray(off, off + len)); curLen += len; off += len; }
                if (len < 255) {                       // packet ends here
                    packets.push(concat(cur)); cur = []; curLen = 0;
                    if (packets.length === need) {
                        if (si !== p.segs.length - 1) throw new Error('ogg_header_shares_page');
                        lastPage = pi;
                        break;
                    }
                    if (packets.length === 1 && si !== p.segs.length - 1) throw new Error('ogg_ident_shares_page');
                }
            }
            if (lastPage >= 0) break;
            if (packets.length === 1 && pi === 0 && cur.length) throw new Error('ogg_ident_shares_page');
        }
        if (lastPage < 0) throw new Error('ogg_no_header');
        return { packets: packets, lastPage: lastPage };
    }
    function oggCodec(ident) {
        if (ident.length >= 7 && ident[0] === 1 && utf8decode(ident.subarray(1, 7)) === 'vorbis') return 'vorbis';
        if (ident.length >= 8 && utf8decode(ident.subarray(0, 8)) === 'OpusHead') return 'opus';
        throw new Error('ogg_codec_unsupported');
    }
    function oggParseComments(pkt, codec) {
        var pos = codec === 'vorbis' ? 7 : 8;
        if (codec === 'vorbis' && !(pkt[0] === 3 && utf8decode(pkt.subarray(1, 7)) === 'vorbis')) throw new Error('ogg_no_comment_header');
        if (codec === 'opus' && utf8decode(pkt.subarray(0, 8)) !== 'OpusTags') throw new Error('ogg_no_comment_header');
        var vlen = rdU32le(pkt, pos); pos += 4;
        var vendor = utf8decode(pkt.subarray(pos, pos + vlen)); pos += vlen;
        var n = rdU32le(pkt, pos); pos += 4;
        var items = [];
        for (var i = 0; i < n && pos + 4 <= pkt.length; i++) {
            var l = rdU32le(pkt, pos); pos += 4;
            items.push(utf8decode(pkt.subarray(pos, pos + l))); pos += l;
        }
        return { vendor: vendor, items: items };
    }
    function oggCommentsPacket(codec, vendor, items) {
        var parts = codec === 'vorbis' ? [Uint8Array.of(3), ascii('vorbis')] : [ascii('OpusTags')];
        var v = utf8(vendor || '');
        parts.push(u32le(v.length), v, u32le(items.length));
        for (var i = 0; i < items.length; i++) { var it = utf8(items[i]); parts.push(u32le(it.length), it); }
        if (codec === 'vorbis') parts.push(Uint8Array.of(1)); // framing bit
        return concat(parts);
    }
    // Lay packets onto pages: ≤255 lacing values per page, a packet that
    // overflows continues on the next page with the continuation flag. Header
    // pages carry granule position 0. One packet per page group — legal, and
    // it keeps "which page holds what" trivially inspectable.
    function oggPaginate(packets, serial, seqStart) {
        var pages = [], seq = seqStart;
        for (var pi = 0; pi < packets.length; pi++) {
            var pkt = packets[pi], lace = [], L = pkt.length;
            while (L >= 255) { lace.push(255); L -= 255; }
            lace.push(L);
            var off = 0, li = 0, first = true;
            while (li < lace.length) {
                var take = lace.slice(li, li + 255), dataLen = 0;
                for (var t = 0; t < take.length; t++) dataLen += take[t];
                var hdr = new Uint8Array(27 + take.length);
                hdr.set(ascii('OggS'), 0);
                hdr[4] = 0; hdr[5] = first ? 0 : 1;               // version, header type (continuation)
                hdr.set(u32le(serial), 14); hdr.set(u32le(seq), 18);
                hdr[26] = take.length; hdr.set(Uint8Array.from(take), 27);
                var page = concat([hdr, pkt.subarray(off, off + dataLen)]);
                page.set(u32le(oggCrc(page, 0, page.length)), 22);
                pages.push(page);
                off += dataLen; li += take.length; seq++; first = false;
            }
        }
        return { pages: pages, nextSeq: seq };
    }
    function flacPictureParse(blk) {
        var pos = 4, mlen = rdU32be(blk, pos); pos += 4;
        var mime = utf8decode(blk.subarray(pos, pos + mlen)); pos += mlen;
        var dlen = rdU32be(blk, pos); pos += 4 + dlen + 16;
        var len = rdU32be(blk, pos); pos += 4;
        return { mime: mime || 'image/jpeg', data: blk.subarray(pos, pos + len).slice() };
    }
    var B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
    function b64enc(u8) {
        var out = [], i, n;
        for (i = 0; i + 2 < u8.length; i += 3) {
            n = (u8[i] << 16) | (u8[i + 1] << 8) | u8[i + 2];
            out.push(B64[n >> 18], B64[(n >> 12) & 63], B64[(n >> 6) & 63], B64[n & 63]);
        }
        if (i < u8.length) {
            n = (u8[i] << 16) | ((i + 1 < u8.length ? u8[i + 1] : 0) << 8);
            out.push(B64[n >> 18], B64[(n >> 12) & 63], i + 1 < u8.length ? B64[(n >> 6) & 63] : '=', '=');
        }
        return out.join('');
    }
    function b64dec(s) {
        var out = new Uint8Array(Math.floor(s.length * 3 / 4)), o = 0, buf = 0, bits = 0;
        for (var i = 0; i < s.length; i++) {
            var v = B64.indexOf(s[i]);
            if (v < 0) continue;
            buf = ((buf << 6) | v) & 0xffffff; bits += 6;
            if (bits >= 8) { bits -= 8; out[o++] = (buf >> bits) & 0xff; }
        }
        return out.subarray(0, o);
    }
    function oggOpen(u8) {
        var pages = oggPages(u8);
        var ident = oggHeaderPackets(u8, pages, 1).packets[0];
        var codec = oggCodec(ident);
        var h = oggHeaderPackets(u8, pages, codec === 'vorbis' ? 3 : 2);
        return { pages: pages, codec: codec, ident: ident, packets: h.packets, lastHeaderPage: h.lastPage };
    }
    /** Tags, vendor, stream facts and the embedded picture of a Vorbis/Opus file. */
    function readOgg(u8) {
        var o = oggOpen(u8);
        var cm = oggParseComments(o.packets[1], o.codec);
        var tags = vorbisFields(cm.items), raw = {}, picture = null;
        for (var i = 0; i < cm.items.length; i++) {
            var key = vorbisKey(cm.items[i]);
            if (!key) continue;
            var val = cm.items[i].substring(key.length + 1);
            if (key === 'METADATA_BLOCK_PICTURE') {
                if (!picture) { try { picture = flacPictureParse(b64dec(val)); } catch (_) {} }
                continue;
            }
            raw[key] = raw[key] === undefined ? val : raw[key] + VORBIS_JOIN + val;   // a repeated key lists all its values
        }
        var info = o.codec === 'vorbis'
            ? { channels: o.ident[11], sampleRate: rdU32le(o.ident, 12) }
            : { channels: o.ident[9], sampleRate: rdU32le(o.ident, 12) };
        return { codec: o.codec, vendor: cm.vendor, channels: info.channels, sampleRate: info.sampleRate, tags: tags, raw: raw, picture: picture, multi: vorbisMulti(cm.items) };
    }
    function writeOgg(u8, ops) {
        var o = oggOpen(u8);
        var cm = oggParseComments(o.packets[1], o.codec);
        var set = cleanSet(ops.set);
        var items = vorbisItems(cm.items, set, !!ops.all, ops.cover !== undefined);
        if (ops.cover && ops.cover.data && ops.cover.data.length) {
            items.push('METADATA_BLOCK_PICTURE=' + b64enc(flacPictureBlock(ops.cover.mime, ops.cover.data)));
        }
        var headers = [oggCommentsPacket(o.codec, cm.vendor, items)];
        if (o.codec === 'vorbis') headers.push(o.packets[2]);       // setup header, verbatim
        var pg = oggPaginate(headers, o.pages[0].serial, 1);
        var parts = [u8.subarray(0, o.pages[0].end)].concat(pg.pages);
        var audioStart = o.pages[o.lastHeaderPage].end;
        parts.push(u8.subarray(audioStart));
        var out = concat(parts);
        // Renumber + re-CRC the audio pages in place (their bytes are otherwise untouched).
        var pos = 0;
        for (var pi = 0; pi < parts.length - 1; pi++) pos += parts[pi].length;
        var seq = pg.nextSeq;
        for (var ai = o.lastHeaderPage + 1; ai < o.pages.length; ai++) {
            var p = o.pages[ai], len = p.end - p.start;
            out.set(u32le(seq++), pos + 18);
            out[pos + 22] = out[pos + 23] = out[pos + 24] = out[pos + 25] = 0;
            out.set(u32le(oggCrc(out, pos, pos + len)), pos + 22);
            pos += len;
        }
        return out;
    }

    /* ════════════════ DISPATCH ════════════════ */
    var WRITERS = { mp3: writeMp3, flac: writeFlac, wav: writeWav, m4a: writeM4a, mov: writeM4a, ogg: writeOgg };
    var MIME = { mp3: 'audio/mpeg', flac: 'audio/flac', wav: 'audio/wav', m4a: 'audio/mp4', mov: 'video/quicktime', ogg: 'audio/ogg' };

    /* `mime` is the file's own type, not the format family's: an MP4 with a
       video track is video/mp4 (it was labelled audio/mp4 before 2026-09-25). */
    function mimeOf(fmt, u8) { return fmt === 'm4a' ? m4aMime(u8) : (MIME[fmt] || 'application/octet-stream'); }
    function editBytes(u8, ops) {
        var fmt = detectFormat(u8);
        var w = WRITERS[fmt];
        if (!w || !WRITABLE[fmt]) throw new Error('write_unsupported:' + fmt);
        var o = ops || {};
        var bytes = w(u8, o);
        // `stamped`: audio frames given a fresh fingerprint (MP3 cover change, see stampMp3);
        // undefined when no stamp was asked for or the format does not need one (M4A and
        // FLAC were measured to take a new cover without it).
        return { bytes: bytes, fmt: fmt, mime: mimeOf(fmt, u8), stamped: (o.stamp && fmt === 'mp3') ? (o.stamped || 0) : undefined };
    }
    /* ════════════════ FILE-LEVEL (browser) API ════════════════ */
    function toU8(x) {
        if (x instanceof Uint8Array) return Promise.resolve(x);
        if (x && typeof x.arrayBuffer === 'function') return x.arrayBuffer().then(function (ab) { return new Uint8Array(ab); });
        if (x instanceof ArrayBuffer) return Promise.resolve(new Uint8Array(x));
        return Promise.reject(new Error('bad_input'));
    }
    function edit(file, ops) {
        return toU8(file).then(function (u8) {
            var r = editBytes(u8, ops);
            return new Blob([r.bytes], { type: r.mime });
        });
    }
    function strip(file, opts) {
        return toU8(file).then(function (u8) {
            var fmt = detectFormat(u8);
            if (!WRITERS[fmt] || !WRITABLE[fmt]) throw new Error('strip_unsupported:' + fmt);
            var ops = { set: {}, cover: (opts && opts.cover === false) ? undefined : null, all: true };
            return new Blob([WRITERS[fmt](u8, ops)], { type: mimeOf(fmt, u8) });
        });
    }

    return {
        detectFormat: detectFormat, WRITABLE: WRITABLE, COVER_CAPABLE: COVER_CAPABLE, MIME: MIME,
        edit: edit, strip: strip, hasVideo: hasVideo,
        // low-level (bytes in / bytes out) — used by the Node round-trip tests
        writeMp3: writeMp3, writeFlac: writeFlac, writeWav: writeWav, writeM4a: writeM4a, writeOgg: writeOgg,
        readOgg: readOgg, oggPages: oggPages, oggCrc: oggCrc,
        readFlacComments: readFlacComments, readId3: readId3, readMp3Tail: readMp3Tail,
        // text of a tag field that claims no encoding (RIFF INFO) or claims UTF-8: see utf8decode
        text: utf8decode,
        riffTagsAfterDataParts: riffTagsAfterDataParts,
        editBytes: editBytes,
        // binary helpers exported for tests
        ab2bstr: ab2bstr, bstr2u8: bstr2u8
    };
});
